/**
 * Supabase API Client
 *
 * This module provides typed data access functions for Supabase.
 * Uses direct Supabase queries (not Drizzle) since we're in the browser.
 *
 * These API functions drive the spec for backend edge functions.
 */

import { EDGE_FUNCTION_HEADERS, EDGE_FUNCTION_INVOKE_REGION } from '@/lib/edge-functions';
import { z } from 'zod';
import { supabase, getSessionUser } from '@/lib/supabase';
import { resolveMediaUrl } from '@/lib/media';
import { digestFrequencySchema } from '@hermes/db/schema';
import type { UserVoiceSettings, NewUserVoiceSettings, Asset as DbAsset } from '@hermes/db/schema';
import type { Asset as UiAsset } from '@/components/assets/AssetCard';

// Notification types surfaced via the community Feed tab. Mirrors the
// `notify-community` edge function dispatcher in hermes-alfie. Used to scope
// the Feed pill badge.
export const FEED_NOTIFICATION_TYPES = [
  'community.post_shared',
  'community.post_mention',
  'community.comment_mention',
  'community.member_joined',
  // Feed-activity types (2026-08 engagement work, hermes migration 471):
  // a new post in a channel you can see, and comments/replies/reactions on
  // your own post or comment.
  'community.channel_post',
  'community.post_comment',
  'community.comment_reply',
  'community.post_reaction',
] as const;

// Feed types that a post view CLEARS — every one carries metadata.post_id, so
// opening the post is proof the reader saw what the notification pointed at.
// `community.member_joined` is deliberately absent: it points at a member, not
// a post, and viewing some post must never erase it.
const POST_SCOPED_NOTIFICATION_TYPES = [
  'community.post_mention',
  'community.comment_mention',
  'community.post_shared',
  'community.channel_post',
  'community.post_comment',
  'community.comment_reply',
  'community.post_reaction',
] as const;

// The two-unread-model partition (2026-08): plain chat messages belong to the
// WATERMARK model (sidebar badge, conversation pills, tab title — driven by
// conversation_participants.last_read_at) and never appear in the bell or
// /inbox. A notification center containing every chat message is a second,
// worse chat list — 22k of 22.9k monthly rows were chat-message noise. Chat
// MENTIONS stay in both models on purpose (item + place), and reading the
// conversation clears the bell item too via the migration 464 bridge.
export const BELL_EXCLUDED_TYPES = ['community.message'] as const;

// ===========================================
// Types (mirroring @hermes/db schemas)
// ===========================================

export interface UserProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  timezone: string;
  preferences: Record<string, unknown>;
  current_organization_id: string | null;
  created_at: string;
  updated_at: string;
  /**
   * 1–4, declared by the builder in the finance skills check. `null` = never asked, which is what
   * that check keys off; `undefined` = the key is absent from this object (an older cached
   * profile, or the self-heal stub below), which is NOT the same thing. Optional because every
   * other construction of this type omits it.
   */
  finance_skill_level?: number | null;
  /**
   * The builder asked Alfie to stop offering help while they work. Written only from settings;
   * NOT NULL in the database with a default of false, so absent here means "an older cached
   * profile", not "switched off".
   */
  alfie_close_in_support_off?: boolean | null;
  /** Topics the builder has retired. Cleared only from settings, never automatically (A-11). */
  alfie_retired_categories?: string[] | null;
}

/** Location fields on the org a member owns (returned by update_my_org_location). */
export interface MyOrgLocation {
  id: string;
  address: string | null;
  location_city: string | null;
  location_state: string | null;
  location_postcode: string | null;
  location_country: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Dashboard {
  id: string;
  user_id: string;
  name: string;
  is_default: boolean;
  widgets: DashboardWidget[];
  created_at: string;
  updated_at: string;
}

export interface DashboardWidget {
  id: string;
  type: 'prime-score' | 'leadership' | 'key-metric' | 'tasks' | 'insights' | 'constraint' | 'action-plan' | 'recent-activity' | 'mantra' | 'greeting' | 'weekly-pulse' | 'lifecycle-stage' | 'pillar-scores' | 'bss-history' | 'pillar-radar' | 'guardrail-alerts' | 'decline-index' | 'innovation-score' | 'assessment-progress' | 'learning-progress' | 'community-stats' | 'leaderboard-rank' | 'alfie-usage' | 'cash-position' | 'revenue-snapshot' | 'profit-margin' | 'financial-health' | 'team-engagement';
  position: number;
  size: 'small' | 'medium' | 'default' | 'wide' | 'full' | 'tall';
  visible: boolean;
  title?: string;
  config?: Record<string, unknown>;
}

export interface PrimePathAssessment {
  id: string;
  user_id: string;
  bss_score: number;
  lifecycle_stage: string;
  years_in_business: number;
  completion_date: string;
  responses: Record<string, unknown>;
  created_at: string;
}

export interface PlannerTask {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  status: 'today' | 'upcoming' | 'completed' | 'archived';
  priority: number;
  due_date: string | null;
  category?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  organization_id: string | null;
  title: string;
  message: string | null;
  type: string;
  is_read: boolean;
  read_at: string | null;
  action_url: string | null;
  action_label: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  snoozed_until: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

/**
 * Notification preferences — snake_case to match Supabase client responses.
 * D3: Zod schema mirroring Drizzle notificationPreferences table (snake_case for Supabase client).
 * @see packages/db/src/schema/community/notification-preferences.ts
 */
export const notificationPreferencesDataSchema = z.object({
  id: z.string().uuid().optional(),
  user_id: z.string().uuid(),
  channel_messages: z.boolean(),
  direct_messages: z.boolean(),
  mentions: z.boolean(),
  reactions: z.boolean(),
  // Feed-activity toggles (hermes migration 469). Default true.
  feed_posts: z.boolean().default(true),
  comments: z.boolean().default(true),
  // The morning move notification (hermes T8, migration 614). Default true.
  coaching_moves: z.boolean().default(true),
  // The "We missed you" re-engagement email (hermes migration 644). Default true.
  reengagement: z.boolean().default(true),
  email_enabled: z.boolean(),
  email_frequency: digestFrequencySchema,
  muted_conversations: z.array(z.string().uuid()),
  quiet_hours_start: z.string().nullable(),
  quiet_hours_end: z.string().nullable(),
  sound_enabled: z.boolean().default(true),
  desktop_push_enabled: z.boolean().default(false),
  mobile_push_enabled: z.boolean().default(false),
  tab_title_enabled: z.boolean().default(true),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type NotificationPreferencesData = z.infer<typeof notificationPreferencesDataSchema>;

// ===========================================
// Diagnostic & Assessment Types
// ===========================================

export interface DiagnosticResult {
  id: string;
  user_id: string;
  diagnostic_type: string;
  scores: Record<string, number>;
  insights: string[];
  recommendations: string[];
  completion_date: string;
  created_at: string;
}

export interface AssessmentResponse {
  id: string;
  user_id: string;
  assessment_id: string;
  question_id: string;
  answer: unknown;
  created_at: string;
}

// ===========================================
// CRM Types
// ===========================================

export interface Contact {
  id: string;
  user_id: string;
  organization_id?: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  title: string | null;
  type: 'lead' | 'prospect' | 'customer' | 'partner' | 'other';
  status: 'active' | 'inactive' | 'archived';
  tags: string[];
  notes: string | null;
  custom_fields: Record<string, unknown>;
  last_contacted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Deal {
  id: string;
  user_id: string;
  contact_id: string | null;
  title: string;
  value: number;
  currency: string;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  probability: number;
  expected_close_date: string | null;
  notes: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  user_id: string;
  organization_id?: string;
  name: string;
  description: string | null;
  status: 'planning' | 'in_progress' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  start_date: string | null;
  end_date: string | null;
  budget: number | null;
  tags: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// ===========================================
// Financial Types
// ===========================================

export interface FinancialMetrics {
  id: string;
  user_id: string;
  organization_id?: string;
  period: string; // YYYY-MM format
  revenue: number;
  expenses: number;
  profit: number;
  cash_flow: number;
  runway_months: number | null;
  arr: number | null;
  mrr: number | null;
  gross_margin: number | null;
  burn_rate: number | null;
  custom_metrics: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface Asset {
  id: string;
  user_id: string;
  organization_id?: string;
  name: string;
  type: 'cash' | 'investment' | 'property' | 'equipment' | 'inventory' | 'receivable' | 'other';
  value: number;
  currency: string;
  acquisition_date: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: string;
  user_id: string;
  contact_id: string | null;
  deal_id: string | null;
  invoice_number: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  amount: number;
  currency: string;
  due_date: string;
  paid_date: string | null;
  line_items: InvoiceLineItem[];
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

// ===========================================
// AI Assistant (Alfie) Types
// ===========================================

export interface AlfieThread {
  id: string;
  user_id: string;
  title: string;
  context_type: 'general' | 'diagnostic' | 'planning' | 'coaching' | 'learning';
  context_id: string | null;
  summary: string | null;
  is_archived: boolean;
  last_message_at: string;
  created_at: string;
  updated_at: string;
}

export interface AlfieMessage {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments: MessageAttachment[];
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface MessageAttachment {
  type: 'file' | 'image' | 'link' | 'widget';
  url?: string;
  name?: string;
  data?: unknown;
}

export interface AlfieInsight {
  id: string;
  user_id: string;
  type: 'recommendation' | 'observation' | 'alert' | 'opportunity';
  category: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high';
  status: 'new' | 'viewed' | 'acted' | 'dismissed';
  action_url: string | null;
  expires_at: string | null;
  created_at: string;
}

// ===========================================
// Learning & Development Types (LMS)
// ===========================================

export interface LearningPath {
  id: string;
  name: string;
  description: string | null;
  short_description: string | null;
  thumbnail_url: string | null;
  banner_url: string | null;
  color: string | null;
  category: string | null;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | null;
  estimated_hours: number | null;
  tags: string[];
  learning_objectives: string[];
  status: 'draft' | 'published' | 'archived';
  is_featured: boolean;
  display_order: number;
  created_by: string | null;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  // Computed/joined fields
  courses?: Course[];
  course_count?: number;
}

export interface Module {
  id: string;
  course_id: string;
  name: string;
  description: string | null;
  order: number;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  lessons?: Lesson[];
  assessments?: Assessment[];
  lesson_count?: number;
}

/** @see packages/db/src/schema/lessons.ts — Drizzle source of truth (camelCase) */
export interface Lesson {
  id: string;
  course_id: string;
  module_id: string | null;
  name: string;
  description: string | null;
  content: string | null;
  content_type: string | null;
  content_url: string | null;
  video_url: string | null;
  video_asset_id: string | null;
  transcript_url: string | null;
  transcript_text: string | null;
  duration_minutes: number | null;
  estimated_minutes: number | null;
  order: number;
  status: string;
  // Lesson-specific (from ClickUp — optional, not always selected)
  lesson_type?: string | null;
  is_required?: boolean | null;
  learning_objectives?: string | null;
  delivery_mode?: string | null;
  estimated_completion_time?: string | null;
  sequence_order?: number | null;
  // Categorization
  pillar?: string | null;
  sub_pillar?: string | null;
  content_domain?: string | null;
  format?: string | null;
  difficulty_level?: string | null;
  // Access & visibility
  access_level?: string | null;
  visibility?: string | null;
  source_system?: string | null;
  // AI & embedding context
  ai_context_role?: string | null;
  embedding_priority?: string | null;
  expert_persona?: string | null;
  // Business context
  prime_path_phase?: string | null;
  business_lifecycle_stage?: string | null;
  strategic_value?: string | null;
  implementation_effort?: string | null;
  // Tags & topics (jsonb arrays)
  content_tags?: string[] | null;
  content_topics?: string[] | null;
  audience_type?: string[] | null;
  // Problem & methodology tagging
  primary_problem_tag?: string | null;
  problem_tags?: string[] | null;
  methodology_phase?: string[] | null;
  industry_tags?: string[] | null;
  resources_urls?: string | null;
  // Metadata
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Joined fields (not in Drizzle schema)
  media?: MediaAsset | null;
  video_media?: {
    small_poster_url: string | null;
    poster_url: string | null;
    thumbnail_url: string | null; // JSON array of VideoThumbnail objects
    blurhash: string | null;
    status: string | null;
    // Added for the prefetch coordinator — null when transcoding hasn't
    // landed yet. Prefetch hooks skip lessons where playback_url is null.
    playback_url?: string | null;
    // Ticket 86d40p593. 'ready' means Cloudflare Stream serves this lesson, so
    // there is no stable URL to warm and the prefetcher must skip it.
    stream_status?: string | null;
    metadata?: Record<string, unknown> | null;
  } | null;
  // Ingested custom thumbnail (lessons.thumbnail_asset_id → media). Display prefers this
  // over the video first-frame poster. Null when no custom thumbnail / not yet ingested.
  thumbnail_media?: {
    small_poster_url: string | null;
    poster_url: string | null;
    blurhash: string | null;
    status: string | null;
  } | null;
  linked_assets?: UiAsset[];
}

export interface CourseThumbnailMedia {
  id: string;
  blurhash: string | null;
  small_poster_url: string | null;
  poster_url: string | null;
  thumbnail_url?: unknown; // JSON array of VideoThumbnail objects
}

/** Poster info from a lesson's video media — used for course card backgrounds */
export interface LessonPoster {
  id: string; // lesson id
  video_media: {
    small_poster_url: string | null;
    poster_url: string | null;
    blurhash: string | null;
  } | null;
}

/** Just enough of a module to put the lessons under it in running order. */
export interface CourseOutlineModule {
  id: string;
  order: number | null;
}

/** Just enough of a lesson to name it, place it, and say how long it runs. */
export interface CourseOutlineLesson {
  id: string;
  name: string;
  order: number | null;
  module_id: string | null;
  duration_minutes: number | null;
  estimated_minutes: number | null;
}

/** The raw rows a running order is built from. Unsorted: the caller owns the flattening. */
export interface CourseLessonOutline {
  modules: CourseOutlineModule[];
  lessons: CourseOutlineLesson[];
}

/** @see packages/db/src/schema/courses.ts — Drizzle source of truth (camelCase) */
export interface Course {
  id: string;
  clickup_id?: string | null;
  name: string;
  description: string | null;
  // Categorization
  pillar: string | null;
  sub_pillar?: string | null;
  course_category?: string | null;
  course_type?: string | null;
  content_domain?: string | null;
  // Display & media
  thumbnail_url: string | null;
  video_url: string | null;
  duration_minutes: number | null;
  lessons_count?: number | null;
  format?: string | null;
  // Course-specific
  presenter_id?: string | null;
  presenter_name?: string | null;
  transcript_url?: string | null;
  assessment_url?: string | null;
  link?: string | null;
  // Learning metadata
  difficulty_level?: string | null;
  delivery_mode?: string | null;
  learning_objectives?: string | null;
  estimated_completion_time?: string | null;
  sequence_order?: number | null;
  // Tags & topics (jsonb arrays)
  content_tags?: string[] | null;
  content_topics?: string[] | null;
  audience_type?: string[] | null;
  // AI & embedding context
  ai_context_role?: string | null;
  expert_persona?: string | null;
  embedding_priority?: string | null;
  // Business context
  prime_path_phase?: string | null;
  business_lifecycle_stage?: string | null;
  strategic_value?: string | null;
  implementation_effort?: string | null;
  // Problem & methodology tagging
  primary_problem_tag?: string | null;
  problem_tags?: string[] | null;
  methodology_phase?: string[] | null;
  resources_urls?: string | null;
  // Status & access
  status: string;
  access_level: string | null;
  /** Module pacing: 'none' | 'completion' (migration 334). */
  drip_mode?: string | null;
  visibility?: string | null;
  source_system?: string | null;
  // Ownership
  organization_id: string | null;
  // Metadata
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Computed/joined fields (not in Drizzle schema)
  estimated_hours?: number | null;
  modules?: Module[];
  lessons?: Lesson[];
  assessments?: Assessment[];
  module_count?: number;
  lesson_count?: number;
  thumbnail_media?: CourseThumbnailMedia | null;
  /**
   * Bare R2 path of the 8-second silent hover teaser, denormalised from the course's first
   * lesson (ticket 86d40p593). Resolve with `resolveMediaUrl` like every other media path.
   *
   * It lives on the COURSE row because the grid query must not embed lessons: that embed was
   * removed after it pulled 254 lesson rows through a nested media join, exceeded the 8s
   * statement timeout and rendered "No Courses Available". Kept in step by a database trigger,
   * not by anything here.
   *
   * Null is the normal state for a course whose first lesson has no clip yet.
   */
  preview_clip_url?: string | null;
  lesson_posters?: LessonPoster[];
  linked_assets?: UiAsset[];
  linked_asset_count?: number;
}

export interface PathEnrollment {
  id: string;
  path_id: string;
  user_id: string;
  status: 'enrolled' | 'in_progress' | 'completed' | 'paused' | 'expired';
  progress_percentage: number;
  completed_courses: number;
  total_courses: number;
  current_course_id: string | null;
  enrolled_at: string;
  started_at: string | null;
  completed_at: string | null;
  last_accessed_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  path?: LearningPath;
}

export interface CourseEnrollment {
  id: string;
  user_id: string;
  course_id: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'paused';
  progress_percentage: number;
  current_lesson_id: string | null;
  completed_lessons_count: number;
  enrolled_at: string;
  started_at: string | null;
  completed_at: string | null;
  last_accessed_at: string | null;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  course?: Course;
}

export interface LessonProgress {
  id: string;
  user_id: string;
  lesson_id: string;
  course_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress_percentage: number;
  time_spent_seconds: number;
  started_at: string | null;
  completed_at: string | null;
  last_accessed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Assessment {
  id: string;
  course_id: string | null;
  module_id: string | null;
  lesson_id: string | null;
  name: string;
  description: string | null;
  instructions: string | null;
  type: 'quiz' | 'exam' | 'practice' | 'survey';
  passing_score: number;
  time_limit: number | null;
  max_attempts: number | null;
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  show_correct_answers: boolean;
  status: 'draft' | 'published' | 'archived';
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  questions?: AssessmentQuestion[];
  question_count?: number;
}

export interface AssessmentQuestion {
  id: string;
  assessment_id: string;
  type: 'multiple_choice' | 'multiple_select' | 'true_false' | 'fill_blank' | 'matching' | 'short_answer' | 'essay';
  question: string;
  explanation: string | null;
  options: unknown[];
  correct_answer: unknown;
  points: number;
  order: number;
  is_required: boolean;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentAttempt {
  id: string;
  assessment_id: string;
  user_id: string;
  attempt_number: number;
  status: 'in_progress' | 'submitted' | 'graded';
  answers: Record<string, unknown>;
  score: number | null;
  max_score: number | null;
  percentage: number | null;
  passed: boolean | null;
  started_at: string;
  submitted_at: string | null;
  time_spent_seconds: number | null;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  assessment?: Assessment;
}

export interface Certificate {
  id: string;
  user_id: string;
  path_id: string | null;
  course_id: string | null;
  certificate_number: string;
  title: string;
  recipient_name: string;
  verification_url: string | null;
  template_data: Record<string, unknown>;
  issued_at: string;
  expires_at: string | null;
  created_at: string;
  // Computed/joined fields
  path?: LearningPath;
  course?: Course;
}

// ===========================================
// Gamification Types
// ===========================================
// NOTE: PostgREST returns snake_case, so these use snake_case field names.
// The Drizzle schemas in @hermes/db use camelCase (JS convention).
// These interfaces are the snake_case API shape for frontend consumption.

export interface Badge {
  id: string;
  name: string;
  description: string;
  code: string;
  icon: string | null;
  image_url: string | null;
  color: string | null;
  rarity: string;
  category: string;
  points: number | null;
  criteria: Record<string, unknown>;
  is_active: boolean | null;
  is_secret: boolean | null;
  display_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  earned_at: string | null;
  context: Record<string, unknown>;
  notified_at: string | null;
  created_at: string | null;
  badge?: Badge;
}

export interface PointTransaction {
  id: string;
  user_id: string;
  points: number;
  source_type: string;
  source_id: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  status: string;
  target_value: number;
  target_metric: string;
  start_date: string | null;
  end_date: string | null;
  reward_points: number | null;
  reward_badge_id: string | null;
  is_active: boolean | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChallengeParticipant {
  id: string;
  challenge_id: string;
  user_id: string;
  progress: number | null;
  joined_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

/** Badge with earned status for display */
export interface BadgeWithEarnedStatus extends Badge {
  is_earned: boolean;
  earned_at?: string | null;
}

/** Leaderboard entry — aggregated, not a direct table row */
export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  organization_id: string | null;
  organization_name: string | null;
  total_points: number;
  badge_count: number;
  courses_completed: number;
}

/** Challenge with participant counts and join status */
export interface ChallengeWithStatus extends Challenge {
  participant_count: number;
  is_joined: boolean;
}

// Legacy type alias for backward compatibility with Learn page
export interface LearningModule {
  id: string;
  learning_path_id: string;
  title: string;
  description: string;
  content_type: 'video' | 'article' | 'quiz' | 'exercise' | 'worksheet';
  content_url: string | null;
  content_data: Record<string, unknown>;
  duration_minutes: number;
  order: number;
  created_at: string;
}

export interface UserLearningProgress {
  id: string;
  user_id: string;
  learning_path_id: string;
  module_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress_percent: number;
  score: number | null;
  time_spent_minutes: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

// ===========================================
// Coaching & Guidance Types
// ===========================================

export interface Coach {
  id: string;
  full_name: string;
  title: string;
  bio: string;
  avatar_url: string | null;
  specialties: string[];
  availability: CoachAvailability[];
  hourly_rate: number | null;
  currency: string;
  timezone: string;
  is_active: boolean;
  rating: number | null;
  total_sessions: number;
  created_at: string;
}

export interface CoachAvailability {
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface GuidanceCall {
  id: string;
  user_id: string;
  coach_id: string | null;
  title: string;
  description: string | null;
  type: 'intro' | 'coaching' | 'strategy' | 'review' | 'emergency';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_at: string;
  duration_minutes: number;
  meeting_url: string | null;
  notes: string | null;
  recording_url: string | null;
  feedback: CallFeedback | null;
  created_at: string;
  updated_at: string;
}

export interface CallFeedback {
  rating: number;
  comment: string | null;
  submitted_at: string;
}

// ===========================================
// Activity & History Types
// ===========================================

export interface Activity {
  id: string;
  user_id: string;
  type: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ActionPlanSession {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  type: 'workshop' | 'webinar' | 'coaching' | 'review' | 'planning';
  status: 'upcoming' | 'in_progress' | 'completed' | 'missed';
  scheduled_at: string;
  duration_minutes: number;
  action_items: ActionItem[];
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionItem {
  id: string;
  title: string;
  completed: boolean;
  due_date: string | null;
}

// ===========================================
// Library Asset Types (ClickUp-synced business assets)
// ===========================================

/**
 * Metadata structure for assets synced from ClickUp.
 * All fields are optional as they may not be present in ClickUp.
 */
export interface LibraryAssetMetadata {
  // ClickUp task tags (synced from task.tags)
  tags?: string[];
  // Last edited timestamp from ClickUp custom field
  last_edited?: string;
  // Optional display/analytics fields (can be added via ClickUp custom fields)
  featured?: boolean;
  is_featured?: boolean;
  downloads?: number;
  views?: number;
  rating?: number;
  file_size?: string;
  // Any other unmapped ClickUp custom fields
  [key: string]: unknown;
}

/**
 * Raw asset shape from the prometheus-assets edge function (Supabase PostgREST).
 * Uses snake_case columns — mirrors {@link DbAsset} from `@hermes/db/schema` (camelCase).
 * Includes joined `media` record not present in the drizzle schema.
 */
export interface LibraryAsset {
  id: string;
  clickup_id: string;
  name: string;
  description: string | null;
  asset_type: string;
  asset_category: string | null; // 085: 54-value dropdown (was not in old interface)
  pillar: string | null;         // 085: renamed from category
  prime_path_phase: string | null;
  strategic_value: string | null;
  link: string | null;
  format: string | null;         // 085: renamed from file_format
  /** Server-computed. The raw ingest-source URLs (download_url /
   *  microsoft_download_url / video_url) are deliberately NOT sent to the client —
   *  they are Google Drive share links, and possession of one is enough to hit
   *  Google's request-access page. See sanitizeAssetRow in the prometheus-assets
   *  edge function. Only their existence was ever used here. */
  has_external_source?: boolean | null;
  thumbnail_url: string | null;  // Legacy — no longer written by pipeline
  // Media delivery fields (denormalized from media table by finalize-asset)
  poster_url: string | null;
  thumbnails: Array<{ index: number; timestamp: number; sm: string; md: string; lg: string }> | null;
  thumbnail_index: number;
  // Joined media record
  media: {
    id: string;
    status: string | null;
    playback_url: string | null;
    poster_url: string | null;
    small_poster_url: string | null;
    thumbnail_url: string | null;
    blurhash: string | null;
    duration_seconds: number | null;
    original_size_bytes: number | null;
    original_mime_type: string | null;
    metadata: unknown;
  } | null;
  // Business classification (added 2026-03-23)
  content_domain: string | null;
  business_category: string | null;
  process_area: string | null;
  sub_pillar: string | null;
  status: 'draft' | 'active' | 'archived';
  access_level: string | null;   // 085: renamed from access_tier
  metadata: LibraryAssetMetadata | null;
  // Tracking fields (from activities table aggregation)
  view_count?: number;
  download_count?: number;
  starred_count?: number;
  last_viewed_at?: string;
  last_downloaded_at?: string;
  // Admin-controlled featured status
  featured?: boolean;
  created_at: string;
  updated_at: string;
}

export interface LibraryAssetFilters {
  assetType?: string;
  category?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

type AssetUIType = 'document' | 'spreadsheet' | 'presentation' | 'video' | 'image' | 'audio' | 'template' | 'sop' | 'code' | 'archive' | 'smart_app' | 'other';

// Maps assets.format column (from ClickUp) to UI display type.
// This is the PRIMARY type source — most assets have a meaningful format value.
const FORMAT_TYPE_MAP: Record<string, AssetUIType> = {
  pdf: 'document',
  document: 'document',
  spreadsheet: 'spreadsheet',
  presentation: 'presentation',
  video: 'video',
  image: 'image',
  audio: 'audio',
};

// Fallback: maps asset_type to UI type when format is null.
// Only 4 records have a meaningful asset_type (template, sop); the rest are "other".
const ASSET_TYPE_MAP: Record<string, AssetUIType> = {
  agreement: 'document',
  ai_skill: 'document',
  avatar: 'image',
  budget: 'spreadsheet',
  calculator: 'spreadsheet',
  certificate: 'document',
  checklist: 'document',
  course: 'video',
  email: 'template',
  form: 'template',
  guideline: 'document',
  invoice: 'template',
  lesson: 'video',
  letter: 'template',
  meeting_agenda: 'template',
  plan: 'document',
  policy: 'document',
  position_description: 'document',
  presentation: 'presentation',
  qbe: 'document',
  register: 'spreadsheet',
  schedule: 'spreadsheet',
  sop: 'sop',
  template: 'template',
  workflow: 'document',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Classify an archive from the media MIME type (same MIME set as the worker's
 * archive classifier). Media MIME is ground truth for archives — the ClickUp
 * `format` field is unreliable (real archives have been tagged `web_page`) —
 * so `transformLibraryAsset` lets this override the format/asset_type maps.
 * The MIME is only populated after the first successful ingest; pre-ingest
 * archives classify as whatever the legacy maps say (usually 'other').
 */
export function archiveKindFromMime(mime: string | null | undefined): 'zip' | 'rar' | null {
  if (!mime) return null;
  const bare = mime.split(';')[0].trim().toLowerCase();
  if (bare === 'application/zip' || bare === 'application/x-zip-compressed') return 'zip';
  if (
    bare === 'application/vnd.rar' ||
    bare === 'application/x-rar-compressed' ||
    bare === 'application/x-rar'
  ) return 'rar';
  return null;
}

// Transform database asset to UI Asset format
export function transformLibraryAsset(dbAsset: LibraryAsset): UiAsset {
  // Safely access metadata with fallback to empty object
  const metadata: LibraryAssetMetadata = dbAsset.metadata ?? {};
  const tags: string[] = [];

  // Extract tags from metadata if available (ClickUp tags)
  if (Array.isArray(metadata.tags) && metadata.tags.length > 0) {
    tags.push(...metadata.tags.filter((t): t is string => typeof t === 'string'));
  }

  // Use asset_type and pillar as fallback tags if no tags found
  if (tags.length === 0) {
    if (dbAsset.asset_type && dbAsset.asset_type !== 'other') {
      tags.push(dbAsset.asset_type.replace(/_/g, ' '));
    }
    if (dbAsset.pillar) {
      tags.push(dbAsset.pillar);
    }
  }

  // Add format as a tag if available and not already present
  if (dbAsset.format && !tags.includes(dbAsset.format)) {
    tags.push(dbAsset.format);
  }

  // Featured flag: prioritize database column over metadata
  const featured = dbAsset.featured ?? metadata.featured ?? metadata.is_featured ?? false;

  // Tracking counters: prioritize database columns over metadata (for backward compatibility)
  const viewCount = dbAsset.view_count ?? (typeof metadata.views === 'number' ? metadata.views : undefined);
  const downloadCount = dbAsset.download_count ?? (typeof metadata.downloads === 'number' ? metadata.downloads : undefined);
  const starredCount = dbAsset.starred_count ?? 0;
  const rating = typeof metadata.rating === 'number' ? metadata.rating : undefined;
  const sizeBytes = dbAsset.media?.original_size_bytes;
  const fileSize = sizeBytes
    ? formatFileSize(sizeBytes)
    : typeof metadata.file_size === 'string' ? metadata.file_size : undefined;

  // Resolve best thumbnail URL — priority matches CourseCard pattern
  // All media paths are bare paths (e.g. /system/xxx/poster.jpg) that need
  // resolveMediaUrl() to prefix with VITE_MEDIA_HOST CDN hostname.
  const thumbIdx = dbAsset.thumbnail_index ?? 0;
  const bestPath =
    (dbAsset.thumbnails && dbAsset.thumbnails.length > 0
      ? dbAsset.thumbnails[thumbIdx]?.lg ?? dbAsset.thumbnails[0]?.lg
      : null) ??
    dbAsset.poster_url ??
    dbAsset.media?.small_poster_url ??
    dbAsset.media?.poster_url ??
    dbAsset.thumbnail_url;
  const resolvedThumbnail = resolveMediaUrl(bestPath);

  // Archives are detected from the ingested media MIME, which beats the
  // unreliable ClickUp format field (see archiveKindFromMime).
  const archiveKind = archiveKindFromMime(dbAsset.media?.original_mime_type);

  return {
    id: dbAsset.id,
    title: dbAsset.name,
    description: dbAsset.description ?? undefined,
    type: archiveKind
      ? 'archive'
      : FORMAT_TYPE_MAP[dbAsset.format ?? ''] || ASSET_TYPE_MAP[dbAsset.asset_type] || 'other',
    archiveKind: archiveKind ?? undefined,
    category: dbAsset.pillar ?? 'Uncategorized',
    tags,
    thumbnail: resolvedThumbnail ?? undefined,
    blurhash: dbAsset.media?.blurhash ?? undefined,
    // Source URL, in priority order: a ready R2-hosted media file, else the external
    // download link (Drive/web), else the Microsoft 365 link. This MUST resolve all
    // three sources — `hasAssetSource` treats presence of `url` as the "has any source"
    // signal and filters out assets without it, so a download-only asset (no media
    // record) would otherwise be hidden from the library. `isIngested` separately flags
    // true R2 media. The URL may be external — the actual download is proxied by id via
    // the prometheus-assets `download-file` action, not this href.
    url: dbAsset.media?.status === 'ready' && dbAsset.media?.playback_url
      ? dbAsset.media.playback_url
      : undefined,
    hasSource: (dbAsset.media?.status === 'ready' && !!dbAsset.media?.playback_url) ||
      dbAsset.has_external_source === true,
    isIngested: dbAsset.media?.status === 'ready',
    fileSize,
    viewCount,
    downloadCount,
    starredCount,
    lastViewedAt: dbAsset.last_viewed_at,
    lastDownloadedAt: dbAsset.last_downloaded_at,
    rating,
    featured,
    assetCategory: dbAsset.asset_category ?? undefined,
    primePathPhase: dbAsset.prime_path_phase ?? undefined,
    strategicValue: dbAsset.strategic_value ?? undefined,
    accessLevel: dbAsset.access_level ?? undefined,
    contentDomain: dbAsset.content_domain ?? undefined,
    businessCategory: dbAsset.business_category ?? undefined,
    processArea: dbAsset.process_area ?? undefined,
    subPillar: dbAsset.sub_pillar ?? undefined,
    createdAt: dbAsset.created_at,
    updatedAt: dbAsset.updated_at,
  };
}

/** A junction row with the asset embedded — `course_assets` or `lesson_assets`. */
type LinkedAssetRow = { asset: LibraryAsset | LibraryAsset[] | null };

/**
 * Flatten, dedupe and sort linked-asset rows from one or more junction queries.
 *
 * A course's resources are course-level ∪ lesson-level: the newer content batch
 * (VA Ready Roadmap, VA Delegation, Life by Design, …) links assets ONLY to
 * lessons, so reading `course_assets` alone returns nothing for them. This is the
 * list-side counterpart of the `course_linked_asset_counts()` RPC that feeds the
 * card badge — the two disagreeing is what made those courses show "4 resources"
 * over an empty drawer.
 *
 * Dedupe is by asset id: the same asset can be linked at both levels, and to more
 * than one lesson of the same course.
 *
 * PostgREST returns the embedded `asset` as an object or an array depending on how
 * it infers the relationship, so accept either.
 */
function collectLinkedAssets(rowSets: Array<LinkedAssetRow[] | null | undefined>): UiAsset[] {
  const byId = new Map<string, LibraryAsset>();
  for (const rows of rowSets) {
    for (const row of rows ?? []) {
      const assets = Array.isArray(row.asset) ? row.asset : row.asset ? [row.asset] : [];
      for (const asset of assets) {
        if (!byId.has(asset.id)) byId.set(asset.id, asset);
      }
    }
  }
  return [...byId.values()]
    .map(transformLibraryAsset)
    .sort((a, b) => a.title.localeCompare(b.title));
}

// Select shape that hydrates a LibraryAsset row (incl. joined media) directly from supabase.
// Kept as a const so callers that embed asset rows inside other queries (e.g. course_assets)
// can stay in sync with the LibraryAsset interface shape above.
export const LIBRARY_ASSET_SELECT = `
  id, clickup_id, name, description, asset_type, asset_category, pillar,
  prime_path_phase, strategic_value, link, format, has_external_source, thumbnail_url,
  poster_url, thumbnails, thumbnail_index, content_domain, business_category,
  process_area, sub_pillar, status, access_level, metadata,
  view_count, download_count, starred_count, last_viewed_at, last_downloaded_at,
  featured, created_at, updated_at,
  media:media_id (
    id, status, playback_url, poster_url, small_poster_url, thumbnail_url,
    blurhash, duration_seconds, original_size_bytes, original_mime_type, metadata
  )
`;

// ===========================================
// Settings & Preferences Types
// ===========================================

export interface UserSettings {
  id: string;
  user_id: string;
  email_notifications: boolean;
  push_notifications: boolean;
  weekly_digest: boolean;
  theme: 'light' | 'dark' | 'system';
  language: string;
  date_format: string;
  time_format: '12h' | '24h';
  first_day_of_week: number;
  custom_settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Voice Settings - imported from Drizzle schema
export type { UserVoiceSettings, NewUserVoiceSettings } from '@hermes/db/schema';
export type { VoiceSession, NewVoiceSession } from '@hermes/db/schema';

// ===========================================
// Role Types
// ===========================================

export type RoleCode =
  | 'super_admin'
  | 'support_admin'
  | 'content_admin'
  | 'community_manager'
  | 'company_admin'
  | 'member'
  | 'readonly'
  | 'external_coach'
  | 'external_consultant'
  | 'external_mentor'
  | 'external_advisor';

export interface UserRoleAssignment {
  id: string;
  user_id: string;
  role_id: string;
  organization_id: string | null;
  target_company_id: string | null;
  status: 'active' | 'suspended' | 'expired';
  assigned_at: string;
  expires_at: string | null;
  role: {
    id: string;
    code: RoleCode;
    name: string;
    scope: 'global' | 'organization' | 'external';
    role_type: 'internal' | 'member' | 'external';
  };
}

// ===========================================
// Media Asset Types
// ===========================================

export type MediaAssetType = 'video' | 'audio' | 'image' | 'document';
export type MediaAssetPurpose =
  | 'lesson_video'
  | 'course_thumbnail'
  | 'path_thumbnail'
  | 'avatar'
  | 'attachment'
  | 'banner'
  | 'badge_image'
  | 'assessment_image';
export type MediaAssetStatus = 'uploading' | 'processing' | 'ready' | 'failed' | 'deleted';

export interface MediaAsset {
  id: string;
  organization_id: string | null;
  uploaded_by: string;
  asset_type: MediaAssetType;
  purpose: MediaAssetPurpose | null;
  original_filename: string;
  original_size_bytes: number;
  original_mime_type: string;
  status: MediaAssetStatus;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  error_message: string | null;
  pipeline_execution_id: string | null;
  storage_bucket: string;
  storage_path: string | null;
  playback_url: string | null;
  /**
   * Cloudflare Stream delivery state (ticket 86d40p593). 'ready' means the
   * verify job fetched a real manifest and a real segment with this
   * environment's signing key — NOT that a copy merely exists. Null on every
   * row that has never been submitted, which is how the player knows to keep
   * using playback_url.
   */
  stream_status: string | null;
  thumbnail_url: string | null;
  poster_url: string | null;
  small_poster_url: string | null;
  duration_seconds: number | null;
  width: number | null;
  height: number | null;
  video_codec: string | null;
  audio_codec: string | null;
  video_bitrate: number | null;
  audio_bitrate: number | null;
  framerate: number | null;
  image_variants: Record<string, string>;
  blurhash: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface InitUploadResponse {
  assetId: string;
  uploadUrl: string;
  token: string;
  path: string;
  expiresIn: number;
}

export interface CompleteUploadResponse {
  assetId: string;
  status: string;
  pipelineExecutionId: string;
  message: string;
}

// ===========================================
// Access Matrix Types
// ===========================================

export interface AccessMatrixItem {
  id: string;
  name: string;
  type: 'asset' | 'course';
  asset_type?: string;
  pillar: string | null;
  status: string;
  access_level: string | null;
  visibility: string | null;
  organization_id: string | null;
  updated_at: string;
}

// Case converters live in @/lib/case-converters so they can be unit-tested
// in isolation. Imported here for the user_voice_settings PostgREST boundary.
import { snakeToCamel, camelToSnake } from '@/lib/case-converters';

// ===========================================
// API Functions
// ===========================================

// ── Inserts that must survive the page closing (Speedies, F-129) ──────────────────────
//
// `pagehide` can't await anything, so the session is cached in memory from every auth event
// (TOKEN_REFRESHED included) and the insert is a plain `fetch` with `keepalive`. If the cached
// token expires within 60 s, nothing is sent: a lost analytics row beats a 401 storm.
let keepaliveAuth: { token: string; userId: string; expiresAtMs: number } | null = null;

function cacheKeepaliveAuth(session: { access_token: string; user: { id: string }; expires_at?: number } | null) {
  keepaliveAuth = session
    ? {
      token: session.access_token,
      userId: session.user.id,
      expiresAtMs: (session.expires_at ?? 0) * 1000,
    }
    : null;
}

let keepalivePrimed = false;

/**
 * Start caching the session for keepalive inserts. Idempotent, and never at import (a module-level
 * subscription would run in every test that loads this file). The Speedy player calls it when it
 * opens, so the cache is warm before the page can close.
 */
export function primeKeepaliveAuth(): void {
  if (keepalivePrimed || typeof window === 'undefined') return;
  keepalivePrimed = true;
  try {
    void supabase.auth.getSession().then(({ data }) => cacheKeepaliveAuth(data.session)).catch(() => {});
    supabase.auth.onAuthStateChange((_event, session) => cacheKeepaliveAuth(session));
  } catch {
    keepalivePrimed = false;
  }
}

/** POST one row to PostgREST with `keepalive`. Fire and forget; never throws. */
function restInsertKeepalive(table: string, row: Record<string, unknown>): void {
  primeKeepaliveAuth();
  const auth = keepaliveAuth;
  if (!auth || auth.expiresAtMs - Date.now() < 60_000) return;
  try {
    void fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${table}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${auth.token}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({ user_id: auth.userId, ...row }),
    }).catch(() => {});
  } catch {
    // A browser that refuses keepalive bodies: drop the row.
  }
}

export const api = {
  // ---- HTTP Methods (for edge function calls) ----
  async get(url: string, config?: { responseType?: 'blob' }) {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1${url}`, {
      method: 'GET',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    return { data: config?.responseType === 'blob' ? await response.blob() : await response.json() };
  },

  async post(url: string, body?: any) {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1${url}`, {
      method: 'POST',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    return { data: await response.json() };
  },

  async put(url: string, body?: any) {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1${url}`, {
      method: 'PUT',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    return { data: await response.json() };
  },

  async delete(url: string) {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1${url}`, {
      method: 'DELETE',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${await response.text()}`);
    return { data: await response.json() };
  },

  // ---- User Profile ----
  async getCurrentUser() {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.warn('Profile not found, attempting self-heal:', error.message);

      // Self-heal: create profile if missing
      const newProfile = {
        id: user.id,
        full_name: user.user_metadata?.full_name || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        timezone: 'UTC',
        preferences: {},
        current_organization_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data: createdProfile, error: createError } = await supabase
        .from('user_profiles')
        .upsert(newProfile)
        .select()
        .single();

      if (createError) {
        console.warn('Failed to self-heal profile:', createError.message);
        // Return stub as fallback
        return newProfile as UserProfile;
      }

      console.info('Profile self-healed successfully');
      return createdProfile as UserProfile;
    }

    return data as UserProfile;
  },

  /**
   * Set the finance skill level ONLY IF it has never been set.
   *
   * THE RACE THIS CLOSES (slice 6, recorded then rather than built). Two tabs: tab A answers the
   * skills check and writes 4. Tab B still shows the dialog from a stale cache, and a `Skip`
   * there writes 2 straight over it — a skip silently overruling a real answer, with the builder
   * seeing no sign of it. The `is('finance_skill_level', null)` filter makes the second write
   * match zero rows instead.
   *
   * SEPARATE FROM `updateUserProfile` on purpose: that is the shared helper behind avatar and
   * name saves, and a conditional filter does not belong on it. This is the one narrow path that
   * needs "first answer wins".
   *
   * A no-op is SUCCESS, not an error. The builder answered somewhere; the level is set; the only
   * thing that did not happen is this tab overwriting it. The caller proceeds either way, which
   * is what `FinanceSkillCheck` already does on a failed write.
   */
  async setFinanceSkillLevelIfUnset(level: number) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_profiles')
      .update({ finance_skill_level: level, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .is('finance_skill_level', null);

    if (error) throw error;
  },

  async updateUserProfile(updates: Partial<UserProfile>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert({
        id: user.id,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as UserProfile;
  },

  /**
   * Upload a new avatar to the public `avatars` bucket and point the profile at
   * it. Each upload uses a unique path so the URL changes (browser/CDN caches
   * bust naturally). Also syncs auth user_metadata so surfaces reading it (the
   * nav) match immediately. Returns the new public URL.
   */
  async uploadAvatar(file: File): Promise<string> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    if (!file.type.startsWith('image/')) {
      throw new Error('Avatar must be an image');
    }
    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      throw new Error('Avatar must be under 5 MB');
    }

    const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
    const path = `${user.id}/${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      // 1 year: avatar paths are content-addressed (a fresh UUID per upload,
      // never overwritten — see `path` above), so the object at any URL is
      // immutable and safe to cache long-term. A change yields a new URL.
      // NOTE: the Supabase public endpoint may still serve `no-cache` regardless
      // — the durable fix is a Cloudflare cache rule on /avatars/* (infra).
      .upload(path, file, { cacheControl: '31536000', upsert: false });
    if (uploadError) throw uploadError;

    const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
    const avatarUrl = pub.publicUrl;

    // Canonical store + keep auth metadata in sync so the nav matches.
    await api.updateUserProfile({ avatar_url: avatarUrl });
    await supabase.auth.updateUser({ data: { avatar_url: avatarUrl } });

    return avatarUrl;
  },

  /**
   * Point the profile at an EXISTING image URL (no upload) — used to re-select
   * the OAuth/sign-in avatar after the member has switched away from it. The
   * OAuth photo URL is read from the auth identity (which a re-login refreshes),
   * never re-uploaded into the avatars bucket. Mirrors uploadAvatar's canonical
   * store + auth-metadata sync so the nav matches immediately.
   */
  async setAvatarUrl(url: string): Promise<string> {
    await api.updateUserProfile({ avatar_url: url });
    await supabase.auth.updateUser({ data: { avatar_url: url } });
    return url;
  },

  /** Read the location on the org this member owns (for prefilling the form). */
  async getMyOrgLocation(): Promise<MyOrgLocation | null> {
    const user = await getSessionUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('organizations')
      .select('id, address, location_city, location_state, location_postcode, location_country')
      .eq('created_by', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.warn('Failed to read org location:', error.message);
      return null;
    }
    return (data as MyOrgLocation | null) ?? null;
  },

  /**
   * Update the location on the member's owned org via the ownership-scoped RPC.
   * The org UPDATE RLS requires role='owner' but members are provisioned as
   * 'admin', so a direct update would fail — the SECURITY DEFINER RPC validates
   * created_by = auth.uid() and writes only the location columns. Changing the
   * address re-triggers the geocode cron, which re-places the directory pin.
   */
  async updateMyOrgLocation(input: {
    address?: string | null;
    city?: string | null;
    state?: string | null;
    postcode?: string | null;
    country?: string | null;
  }): Promise<MyOrgLocation> {
    const { data, error } = await supabase.rpc('update_my_org_location', {
      p_address: input.address ?? null,
      p_city: input.city ?? null,
      p_state: input.state ?? null,
      p_postcode: input.postcode ?? null,
      p_country: input.country ?? null,
    });
    if (error) throw error;
    // RPC RETURNS TABLE -> array with one row.
    const row = Array.isArray(data) ? data[0] : data;
    return row as MyOrgLocation;
  },

  // ---- Dashboards ----
  async getDashboards() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('dashboards')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch dashboards:', error.message);
      return [];
    }

    return data as Dashboard[];
  },

  async createDashboard(name: string, isDefault = false) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('dashboards')
      .insert({
        user_id: user.id,
        name,
        is_default: isDefault,
        widgets: [],
      })
      .select()
      .single();

    if (error) throw error;
    return data as Dashboard;
  },

  async updateDashboard(id: string, updates: Partial<Dashboard>) {
    const { data, error } = await supabase
      .from('dashboards')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Dashboard;
  },

  async deleteDashboard(id: string) {
    const { error } = await supabase
      .from('dashboards')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async setDefaultDashboard(id: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Clear existing default
    await supabase
      .from('dashboards')
      .update({ is_default: false })
      .eq('user_id', user.id);

    // Set new default
    const { data, error } = await supabase
      .from('dashboards')
      .update({ is_default: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Dashboard;
  },

  // ---- Prime Path Assessment ----
  async getLatestPrimeScore() {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('prime_path_assessments')
      .select('*')
      .eq('user_id', user.id)
      .order('completion_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch prime score:', error.message);
      return null;
    }

    return data as PrimePathAssessment;
  },

  async getPrimeScoreHistory(limit = 12) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('prime_path_assessments')
      .select('*')
      .eq('user_id', user.id)
      .order('completion_date', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch prime score history:', error.message);
      return [];
    }

    return data as PrimePathAssessment[];
  },

  // ---- Diagnostics ----
  // Note: These legacy methods query diagnostic_responses table
  // For BSS diagnostics, use bss-calculator API hooks instead
  async getDiagnosticResults(diagnosticType?: string) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('diagnostic_responses')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (diagnosticType) {
      query = query.eq('diagnostic_type', diagnosticType);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch diagnostic results:', error.message);
      return [];
    }

    return data as DiagnosticResult[];
  },

  async getLatestDiagnostic(diagnosticType: string) {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('diagnostic_responses')
      .select('*')
      .eq('user_id', user.id)
      .eq('diagnostic_type', diagnosticType)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch diagnostic:', error.message);
      return null;
    }

    return data as DiagnosticResult;
  },

  async saveDiagnosticResult(result: Omit<DiagnosticResult, 'id' | 'user_id' | 'created_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('diagnostic_responses')
      .insert({
        user_id: user.id,
        ...result,
      })
      .select()
      .single();

    if (error) throw error;
    return data as DiagnosticResult;
  },

  // ---- Tasks ----
  async getTodayTasks(limit = 10) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('planner_tasks')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'today')
      .order('priority', { ascending: true })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch tasks:', error.message);
      return [];
    }

    return data as PlannerTask[];
  },

  async getAllTasks(status?: PlannerTask['status']) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('planner_tasks')
      .select('*')
      .eq('user_id', user.id)
      .order('priority', { ascending: true });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch tasks:', error.message);
      return [];
    }

    return data as PlannerTask[];
  },

  async createTask(task: Omit<PlannerTask, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('planner_tasks')
      .insert({
        user_id: user.id,
        ...task,
      })
      .select()
      .single();

    if (error) throw error;
    return data as PlannerTask;
  },

  async updateTask(id: string, updates: Partial<PlannerTask>) {
    const { data, error } = await supabase
      .from('planner_tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as PlannerTask;
  },

  async deleteTask(id: string) {
    const { error } = await supabase
      .from('planner_tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Notifications — Phase 5 ----
  async getNotifications(params?: {
    limit?: number;
    cursor?: string;
    filter?: 'all' | 'unread' | 'mentions' | 'actions';
  }) {
    const user = await getSessionUser();
    if (!user) return [];
    const limit = params?.limit ?? 20;

    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .is('archived_at', null)
      .not('type', 'in', `(${BELL_EXCLUDED_TYPES.map(t => `"${t}"`).join(',')})`)
      .or('snoozed_until.is.null,snoozed_until.lte.now()')
      .order('created_at', { ascending: false })
      .limit(limit);

    // Filter. "Mentions" means every mention flavour — chat, post AND comment;
    // the chat-only filter hid feed mentions from the one place users looked.
    if (params?.filter === 'unread') query = query.eq('is_read', false);
    if (params?.filter === 'mentions') {
      query = query.in('type', [
        'community.mention',
        'community.post_mention',
        'community.comment_mention',
      ]);
    }
    // "Actions" = rows the member is expected to act on. Every event
    // lifecycle type belongs here alongside the reminder: a moved, cancelled
    // or newly-recorded session all change what the member has to do next.
    // The morning move (hermes T8) is the one coaching type: it asks for an answer.
    if (params?.filter === 'actions') {
      query = query.in('type', [
        'community.lead_received',
        'community.event_reminder',
        'community.event_updated',
        'community.event_cancelled',
        'community.event_recording_ready',
        'coaching.move',
      ]);
    }

    // Cursor pagination
    if (params?.cursor) query = query.lt('created_at', params.cursor);

    const { data, error } = await query;
    if (error) {
      console.warn('Failed to fetch notifications:', error.message);
      return [];
    }
    return data as Notification[];
  },

  async getUnreadNotificationCount() {
    const user = await getSessionUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .is('archived_at', null)
      .not('type', 'in', `(${BELL_EXCLUDED_TYPES.map(t => `"${t}"`).join(',')})`)
      .or('snoozed_until.is.null,snoozed_until.lte.now()');

    if (error) {
      console.warn('Failed to fetch notification count:', error.message);
      return 0;
    }
    return count || 0;
  },

  async getUnreadFeedCount() {
    const user = await getSessionUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .is('archived_at', null)
      .in('type', FEED_NOTIFICATION_TYPES)
      .or('snoozed_until.is.null,snoozed_until.lte.now()');

    if (error) {
      console.warn('Failed to fetch feed notification count:', error.message);
      return 0;
    }
    return count || 0;
  },

  /**
   * Entity-viewed clearing: opening a post's page marks that post's
   * notifications (post/comment mentions, shares) read — the reader has seen
   * the thing the notification pointed at. Replaces the old bulk
   * "mark ALL feed types read on Feed-tab click" side effect, which erased
   * mention records without any navigation (the "my notification disappeared
   * and I can't find the comment" bug). Every type in
   * POST_SCOPED_NOTIFICATION_TYPES carries metadata.post_id (notify-community
   * handlers).
   */
  async markNotificationsReadForPost(postId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .in('type', POST_SCOPED_NOTIFICATION_TYPES)
      .eq('metadata->>post_id', postId);
    if (error) throw error;
  },

  async markNotificationRead(id: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * The in-app click (21 Sep 2026).
   *
   * `push_clicked_at` is written only by the service-worker beacon, so a notification opened
   * from the bell or the inbox counted as READ and never as clicked — `admin_analytics()`'s
   * notification funnel was push-only and undercounted every desktop session. `clicked_at` is
   * the union column the funnel now reads; `click_source` keeps the split.
   *
   * `.is('clicked_at', null)` makes first click win: re-opening a notification is not a second
   * click, and a row already clicked from a push must not be rewritten as in-app. Marking read
   * is left to `markNotificationRead`, which the bell already calls — this writes the click and
   * nothing else, so an unread-count optimistic update cannot be confused by it.
   *
   * Never throws: a lost analytics write must not stop the navigation the click triggered.
   */
  async recordNotificationClick(id: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ clicked_at: new Date().toISOString(), click_source: 'in_app' })
      .eq('id', id)
      .is('clicked_at', null);
    if (error) console.warn('[recordNotificationClick] not recorded:', error.message);
  },

  async markAllNotificationsRead() {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('is_read', false);
    if (error) throw error;
  },

  async archiveNotification(id: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async snoozeNotification(id: string, until: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ snoozed_until: until })
      .eq('id', id);
    if (error) throw error;
  },

  // ---- Notification Preferences ----
  async getNotificationPreferences(): Promise<NotificationPreferencesData | null> {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async upsertNotificationPreferences(prefs: Partial<NotificationPreferencesData>): Promise<void> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Row-creation guard: with NO prefs row, the mention-email gate in
    // notify-community treats email as ON (`undefined !== false`). A partial
    // upsert that CREATES the row would let email_enabled take the column
    // default (false) — silently opting the user out of mention emails as a
    // side effect of an unrelated toggle (this is exactly how the push
    // banner de-emailed 125 members). Seed it true when creating the row; an
    // explicit email_enabled in the patch always wins, and an existing row
    // (a real recorded choice) is never touched.
    const { data: existing } = await supabase
      .from('notification_preferences')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle();
    const creationSeed = existing ? {} : { email_enabled: true };

    const { error } = await supabase
      .from('notification_preferences')
      .upsert(
        { ...creationSeed, ...prefs, user_id: user.id },
        { onConflict: 'user_id' },
      );

    if (error) throw error;
  },

  // ---- Contacts ----
  async getContacts(filters?: { type?: Contact['type']; status?: Contact['status']; search?: string }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('contacts')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (filters?.type) {
      query = query.eq('type', filters.type);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch contacts:', error.message);
      return [];
    }

    return data as Contact[];
  },

  async getContact(id: string) {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Contact;
  },

  async createContact(contact: Omit<Contact, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('contacts')
      .insert({
        user_id: user.id,
        ...contact,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Contact;
  },

  async updateContact(id: string, updates: Partial<Contact>) {
    const { data, error } = await supabase
      .from('contacts')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Contact;
  },

  async deleteContact(id: string) {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Deals ----
  async getDeals(filters?: { stage?: Deal['stage']; contact_id?: string }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('deals')
      .select('*, contact:contacts(*)')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (filters?.stage) {
      query = query.eq('stage', filters.stage);
    }
    if (filters?.contact_id) {
      query = query.eq('contact_id', filters.contact_id);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch deals:', error.message);
      return [];
    }

    return data as (Deal & { contact: Contact | null })[];
  },

  async getDeal(id: string) {
    const { data, error } = await supabase
      .from('deals')
      .select('*, contact:contacts(*)')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Deal & { contact: Contact | null };
  },

  async createDeal(deal: Omit<Deal, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('deals')
      .insert({
        user_id: user.id,
        ...deal,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Deal;
  },

  async updateDeal(id: string, updates: Partial<Deal>) {
    const { data, error } = await supabase
      .from('deals')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Deal;
  },

  async deleteDeal(id: string) {
    const { error } = await supabase
      .from('deals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Projects ----
  async getProjects(filters?: { status?: Project['status'] }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('projects')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch projects:', error.message);
      return [];
    }

    return data as Project[];
  },

  async getProject(id: string) {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Project;
  },

  async createProject(project: Omit<Project, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        ...project,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Project;
  },

  async updateProject(id: string, updates: Partial<Project>) {
    const { data, error } = await supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Project;
  },

  async deleteProject(id: string) {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Financial Metrics ----
  async getFinancialMetrics(limit = 12) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('financial_metrics')
      .select('*')
      .eq('user_id', user.id)
      .order('period', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch financial metrics:', error.message);
      return [];
    }

    return data as FinancialMetrics[];
  },

  async getLatestFinancialMetrics() {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('financial_metrics')
      .select('*')
      .eq('user_id', user.id)
      .order('period', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch financial metrics:', error.message);
      return null;
    }

    return data as FinancialMetrics;
  },

  async saveFinancialMetrics(metrics: Omit<FinancialMetrics, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('financial_metrics')
      .upsert({
        user_id: user.id,
        ...metrics,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as FinancialMetrics;
  },

  // ---- Assets ----
  async getAssets(type?: Asset['type']) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('assets')
      .select('*')
      .eq('user_id', user.id)
      .order('value', { ascending: false });

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch assets:', error.message);
      return [];
    }

    return data as Asset[];
  },

  async createAsset(asset: Omit<Asset, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('assets')
      .insert({
        user_id: user.id,
        ...asset,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Asset;
  },

  async updateAsset(id: string, updates: Partial<Asset>) {
    const { data, error } = await supabase
      .from('assets')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Asset;
  },

  async deleteAsset(id: string) {
    const { error } = await supabase
      .from('assets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Library Assets (ClickUp-synced business assets) ----
  async getLibraryAssets(filters?: LibraryAssetFilters) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { assets: [], total: 0 };

    // Build query params — fetch entire library for client-side pagination
    const params = new URLSearchParams();
    params.set('limit', '1000');
    if (filters?.assetType) params.set('asset_type', filters.assetType);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.offset) params.set('offset', filters.offset.toString());

    const queryString = params.toString();
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prometheus-assets${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch assets' }));
      console.warn('Failed to fetch library assets:', error);
      return { assets: [], total: 0 };
    }

    const data = await response.json();

    // Transform assets to UI format
    const transformedAssets = (data.assets || []).map(transformLibraryAsset);

    return {
      assets: transformedAssets,
      total: data.total || transformedAssets.length,
    };
  },

  // ---- Invoices ----
  async getInvoices(filters?: { status?: Invoice['status']; contact_id?: string }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('invoices')
      .select('*, contact:contacts(*)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.contact_id) {
      query = query.eq('contact_id', filters.contact_id);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch invoices:', error.message);
      return [];
    }

    return data as (Invoice & { contact: Contact | null })[];
  },

  async createInvoice(invoice: Omit<Invoice, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('invoices')
      .insert({
        user_id: user.id,
        ...invoice,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Invoice;
  },

  async updateInvoice(id: string, updates: Partial<Invoice>) {
    const { data, error } = await supabase
      .from('invoices')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Invoice;
  },

  // ---- Alfie (AI Assistant) ----
  async getAlfieThreads(limit = 20) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('alfie_threads')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_archived', false)
      .order('last_message_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch Alfie threads:', error.message);
      return [];
    }

    return data as AlfieThread[];
  },

  async createAlfieThread(title: string, contextType: AlfieThread['context_type'] = 'general', contextId?: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('alfie_threads')
      .insert({
        user_id: user.id,
        title,
        context_type: contextType,
        context_id: contextId || null,
        is_archived: false,
        last_message_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as AlfieThread;
  },

  async archiveAlfieThread(id: string) {
    const { error } = await supabase
      .from('alfie_threads')
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async getAlfieMessages(threadId: string, limit = 50) {
    const { data, error } = await supabase
      .from('alfie_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch Alfie messages:', error.message);
      return [];
    }

    return data as AlfieMessage[];
  },

  async sendAlfieMessage(threadId: string, content: string, attachments: MessageAttachment[] = []) {
    const { data, error } = await supabase
      .from('alfie_messages')
      .insert({
        thread_id: threadId,
        role: 'user',
        content,
        attachments,
        metadata: {},
      })
      .select()
      .single();

    if (error) throw error;

    // Update thread last_message_at
    await supabase
      .from('alfie_threads')
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', threadId);

    return data as AlfieMessage;
  },

  async getAlfieInsights(limit = 10) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('alfie_insights')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['new', 'viewed'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch Alfie insights:', error.message);
      return [];
    }

    return data as AlfieInsight[];
  },

  async updateAlfieInsightStatus(id: string, status: AlfieInsight['status']) {
    const { error } = await supabase
      .from('alfie_insights')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Learning Paths (LMS) ----
  async getLearningPaths(category?: string) {
    let query = supabase
      .from('learning_paths')
      .select(`
        *,
        path_courses (
          course_id,
          order,
          courses (
            id,
            name,
            thumbnail_url
          )
        )
      `)
      .eq('status', 'published')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch learning paths:', error.message);
      return [];
    }

    // Transform to include course_count
    return (data || []).map((path: Record<string, unknown>) => ({
      ...path,
      course_count: (path.path_courses as unknown[])?.length || 0,
      courses: ((path.path_courses as Array<{ courses: unknown; order?: number }>) || [])
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((pc) => pc.courses),
    })) as LearningPath[];
  },

  async getLearningPath(id: string) {
    const { data, error } = await supabase
      .from('learning_paths')
      .select(`
        *,
        path_courses (
          course_id,
          order,
          is_required,
          courses (
            id,
            name,
            description,
            thumbnail_url,
            preview_clip_url,
            pillar,
            status,
            estimated_hours,
            thumbnail_media:media!courses_thumbnail_asset_id_fkey (
              id,
              blurhash,
              small_poster_url,
              poster_url
            ),
            lesson_posters:lessons!lessons_course_id_courses_id_fk (
              id,
              video_media:media!lessons_video_asset_id_media_id_fk (
                small_poster_url,
                poster_url,
                blurhash
              )
            ),
            modules (
              id,
              name,
              description,
              order,
              status,
              lessons (
                id,
                name,
                description,
                content_type,
                video_url,
                duration_minutes,
                order,
                status
              )
            )
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    // Transform to include courses with modules
    const pathData = data as Record<string, unknown>;
    return {
      ...pathData,
      course_count: (pathData.path_courses as unknown[])?.length || 0,
      courses: ((pathData.path_courses as Array<{ courses: Course; order?: number; is_required?: boolean }>) || [])
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((pc) => ({
          ...pc.courses,
          modules: (pc.courses.modules || [])
            .sort((a: Module, b: Module) => (a.order ?? 0) - (b.order ?? 0))
            .map((m: Module) => ({
              ...m,
              lessons: (m.lessons || []).sort((a: Lesson, b: Lesson) => (a.order ?? 0) - (b.order ?? 0)),
            })),
        })),
    } as LearningPath & { courses: Course[] };
  },

  async getCourses(pillar?: string) {
    // NOTE: the `lesson_posters` embed was removed from this LIST query.
    // It pulled every published lesson of every course (254 rows) plus a nested media
    // join, purely to supply thumbnail fallback #3 — behind `thumbnail_media`, the
    // curator-chosen image CourseCard already prefers. Embedding it forced PostgREST to
    // evaluate `lessons_select` RLS, whose correlated EXISTS re-runs the whole courses
    // policy per lesson row. That pushed this request past the 8s statement_timeout on
    // the `authenticated` role, returning 500 / SQLSTATE 57014 — which the UI rendered
    // as "No Courses Available", indistinguishable from an empty result.
    // Migration 397 hoisted the caller-only RLS calls (2029ms → 1308ms); dropping this
    // embed removes the lessons scan from the list query entirely.
    // The course DETAIL query still embeds lessons — it fetches one course, not 33.
    let query = supabase
      .from('courses')
      .select(`
        *,
        thumbnail_media:media!courses_thumbnail_asset_id_fkey (
          id,
          blurhash,
          small_poster_url,
          poster_url
        ),
        course_assets (asset_id)
      `)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (pillar) {
      query = query.eq('pillar', pillar);
    }

    // Asset counts come from the course_linked_asset_counts RPC: course-level ∪
    // lesson-level assets in one SECURITY DEFINER pass. Embedding lesson_assets here
    // would re-run correlated RLS per row (the 57014 "No Courses Available" incident).
    // The course_assets embed stays as the fallback so a frontend deployed ahead of
    // the migration (or an RPC failure) degrades to the old course-level-only count.
    const [{ data, error }, countsRes] = await Promise.all([
      query,
      supabase.rpc('course_linked_asset_counts'),
    ]);
    if (error) throw error;

    const rpcCounts = new Map<string, number>(
      ((countsRes.data ?? []) as Array<{ course_id: string; asset_count: number }>)
        .map((r) => [r.course_id, r.asset_count]),
    );
    if (countsRes.error) {
      console.warn('course_linked_asset_counts RPC unavailable, using embed counts:', countsRes.error.message);
    }

    const rows = (data ?? []) as unknown as Array<Course & { course_assets?: Array<{ asset_id: string }> }>;
    return rows.map(({ course_assets, ...rest }) => ({
      ...rest,
      linked_asset_count: rpcCounts.get(rest.id) ?? course_assets?.length ?? 0,
    })) as Course[];
  },

  /**
   * A course's resources: course-level ∪ lesson-level, deduped.
   *
   * Both halves are scoped to the ONE course and hit an index
   * (`idx_course_assets_course_id`, `idx_lessons_course_id` for the embedded
   * filter, `idx_lesson_assets_lesson_id` for the junction), and they run in a
   * single round trip. This is deliberately NOT the shape that caused the
   * "No Courses Available" timeout — that was lesson/asset embeds inside the
   * courses LIST query, where RLS re-ran per course row. Fanning out across
   * many courses must keep using `course_linked_asset_counts()`.
   *
   * Stays RLS-bound rather than reusing that RPC: the RPC is SECURITY DEFINER and
   * intentionally coarse (counts only, no per-tier filtering), so returning rows
   * through it would expose asset titles and download URLs across tiers.
   */
  async getCourseLinkedAssets(courseId: string): Promise<UiAsset[]> {
    const [courseRes, lessonRes] = await Promise.all([
      supabase
        .from('course_assets')
        .select(`asset:assets(${LIBRARY_ASSET_SELECT})`)
        .eq('course_id', courseId),
      supabase
        .from('lesson_assets')
        .select(`asset:assets(${LIBRARY_ASSET_SELECT}), lessons!inner(course_id)`)
        .eq('lessons.course_id', courseId),
    ]);
    if (courseRes.error) throw courseRes.error;
    if (lessonRes.error) throw lessonRes.error;
    return collectLinkedAssets([
      courseRes.data as unknown as LinkedAssetRow[],
      lessonRes.data as unknown as LinkedAssetRow[],
    ]);
  },

  async getLessonLinkedAssets(lessonId: string): Promise<UiAsset[]> {
    const { data, error } = await supabase
      .from('lesson_assets')
      .select(`asset:assets(${LIBRARY_ASSET_SELECT})`)
      .eq('lesson_id', lessonId);
    if (error) throw error;
    return collectLinkedAssets([data as unknown as LinkedAssetRow[]]);
  },

  async getCourse(id: string) {
    // Fetch course with modules (without nested lessons to avoid 400 error)
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        thumbnail_media:media!courses_thumbnail_asset_id_fkey (
          id,
          blurhash,
          small_poster_url,
          poster_url
        ),
        modules (
          id,
          name,
          description,
          order,
          status,
          created_at,
          updated_at
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    const courseData = data as Course;

    // Fetch ALL lessons for this course in a single query (with optional media join)
    const { data: allLessons, error: lessonsError } = await supabase
      .from('lessons')
      .select(`
        id, name, description, content, content_type, content_url, video_url, video_asset_id,
        duration_minutes, estimated_minutes, order, status, module_id, thumbnail_url,
        transcript_url, transcript_text, course_id, created_at, updated_at,
        video_media:media!lessons_video_asset_id_media_id_fk (
          small_poster_url,
          poster_url,
          thumbnail_url,
          blurhash,
          status,
          playback_url,
          stream_status,
          metadata
        ),
        thumbnail_media:media!lessons_thumbnail_asset_id_media_id_fk (
          small_poster_url,
          poster_url,
          blurhash,
          status
        )
      `)
      .eq('course_id', id)
      .order('order', { ascending: true });

    if (lessonsError) throw lessonsError;

    // Fetch published assessments for this course (graceful — empty array on error)
    let courseAssessments: Assessment[] = [];
    try {
      const { data: assessmentData } = await supabase
        .from('assessments')
        .select('*')
        .eq('course_id', id)
        .eq('status', 'published')
        .order('created_at', { ascending: true });
      courseAssessments = (assessmentData ?? []) as Assessment[];
    } catch {
      // Non-fatal — assessments just won't show
    }

    // Fetch linked assets: course-level ∪ lesson-level, deduped (graceful — empty
    // array on error). `allLessons` is already in hand from the query above, so the
    // lesson half is a plain indexed `lesson_id IN (…)` with no join and no extra
    // round trip. Must stay a union: the newer content batch links assets only to
    // lessons, so `course_assets` alone renders an empty resources panel for them.
    let linkedAssets: UiAsset[] = [];
    try {
      const lessonIds = (allLessons ?? []).map((lesson) => lesson.id as string);
      const [courseRes, lessonRes] = await Promise.all([
        supabase
          .from('course_assets')
          .select(`asset:assets(${LIBRARY_ASSET_SELECT})`)
          .eq('course_id', id),
        lessonIds.length > 0
          ? supabase
            .from('lesson_assets')
            .select(`asset:assets(${LIBRARY_ASSET_SELECT})`)
            .in('lesson_id', lessonIds)
          : Promise.resolve({ data: null }),
      ]);
      linkedAssets = collectLinkedAssets([
        courseRes.data as unknown as LinkedAssetRow[],
        lessonRes.data as unknown as LinkedAssetRow[],
      ]);
    } catch {
      // Non-fatal — linked assets just won't show
    }

    // Separate lessons by module_id
    const lessonsByModule = new Map<string | null, Lesson[]>();
    (allLessons || []).forEach((lesson) => {
      const moduleId = lesson.module_id || null;
      if (!lessonsByModule.has(moduleId)) {
        lessonsByModule.set(moduleId, []);
      }
      lessonsByModule.get(moduleId)!.push(lesson as unknown as Lesson);
    });

    // Group assessments by module_id
    const assessmentsByModule = new Map<string | null, Assessment[]>();
    courseAssessments.forEach((a) => {
      const moduleId = a.module_id || null;
      if (!assessmentsByModule.has(moduleId)) {
        assessmentsByModule.set(moduleId, []);
      }
      assessmentsByModule.get(moduleId)!.push(a);
    });

    // Attach lessons and assessments to their modules
    const sortedModules = (courseData.modules || [])
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((m) => ({
        ...m,
        lessons: (lessonsByModule.get(m.id) || []).sort((a: Lesson, b: Lesson) => (a.order ?? 0) - (b.order ?? 0)),
        assessments: assessmentsByModule.get(m.id) || [],
      }));

    // If no modules exist but there are unassigned lessons, create synthetic module
    const unassignedLessons = lessonsByModule.get(null) || [];
    const unassignedAssessments = assessmentsByModule.get(null) || [];
    if (sortedModules.length === 0 && (unassignedLessons.length > 0 || unassignedAssessments.length > 0)) {
      sortedModules.push({
        id: 'unassigned',
        course_id: courseData.id,
        name: 'Course Content',
        description: null,
        order: 0,
        status: 'published' as const,
        created_at: courseData.created_at,
        updated_at: courseData.updated_at,
        lessons: unassignedLessons,
        assessments: unassignedAssessments,
      });
    }

    // Course-level assessments: those with no module_id (or module_id not matching any module)
    const moduleIdSet = new Set(sortedModules.map(m => m.id));
    const courseLevelAssessments = courseAssessments.filter(
      a => !a.module_id || !moduleIdSet.has(a.module_id),
    );

    // Build lesson_posters from fetched lessons (mirrors the shape used by CourseCard)
    const lessonPosters = (allLessons || []).map((lesson: any) => ({
      id: lesson.id,
      video_media: lesson.video_media ?? null,
    }));

    return {
      ...courseData,
      modules: sortedModules,
      assessments: courseLevelAssessments,
      lesson_posters: lessonPosters,
      linked_assets: linkedAssets,
    } as Course;
  },

  /**
   * A course's modules and lessons, with nothing joined to them.
   *
   * `getCourse` answers the same question, but it drags four more round trips along with it
   * (video and thumbnail media, assessments, course assets, lesson assets) because the course
   * page needs all of that. Home needs a lesson's name, its place in the running order and its
   * length, so it asks for exactly those columns: two indexed selects, no embeds.
   */
  async getCourseLessonOutline(courseId: string): Promise<CourseLessonOutline> {
    const [modulesRes, lessonsRes] = await Promise.all([
      supabase.from('modules').select('id, order').eq('course_id', courseId),
      supabase
        .from('lessons')
        .select('id, name, order, module_id, duration_minutes, estimated_minutes')
        .eq('course_id', courseId),
    ]);

    if (modulesRes.error) throw modulesRes.error;
    if (lessonsRes.error) throw lessonsRes.error;

    return {
      modules: (modulesRes.data ?? []) as unknown as CourseOutlineModule[],
      lessons: (lessonsRes.data ?? []) as unknown as CourseOutlineLesson[],
    };
  },

  async getLesson(id: string) {
    const { data, error } = await supabase
      .from('lessons')
      .select('*, media:video_asset_id(*)')
      .eq('id', id)
      .single();

    if (error) throw error;

    // Fetch lesson-level linked assets via lesson_assets junction
    // (graceful — empty array on error so the lesson still renders).
    let linkedAssets: UiAsset[] = [];
    try {
      const { data: linkedAssetRows } = await supabase
        .from('lesson_assets')
        .select(`asset:assets(${LIBRARY_ASSET_SELECT})`)
        .eq('lesson_id', id);
      const dbRows = ((linkedAssetRows ?? []) as unknown as Array<{
        asset: LibraryAsset | LibraryAsset[] | null;
      }>)
        .flatMap((r) => (Array.isArray(r.asset) ? r.asset : r.asset ? [r.asset] : []));
      linkedAssets = dbRows
        .map(transformLibraryAsset)
        .sort((a, b) => a.title.localeCompare(b.title));
    } catch {
      // Non-fatal — linked assets just won't show
    }

    console.log('📘 [getLesson] Lesson:', data.name);
    console.log('  - hasContent:', !!data.content, 'length:', data.content?.length || 0);
    console.log('  - hasVideo:', !!data.video_url);
    console.log('  - hasAssetVideo:', !!data.video_asset_id, 'status:', (data.media as { status?: string } | null)?.status);
    console.log('  - hasTranscript:', !!data.transcript_url);
    console.log('  - hasTranscriptText:', !!data.transcript_text, 'length:', data.transcript_text?.length || 0);
    console.log('  - linkedAssets:', linkedAssets.length);
    return { ...data, linked_assets: linkedAssets } as Lesson;
  },

  // ---- Path Enrollments ----
  async getMyEnrollments() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('path_enrollments')
      .select(`
        *,
        path:learning_paths (
          id,
          name,
          short_description,
          thumbnail_url,
          category,
          difficulty,
          estimated_hours
        )
      `)
      .eq('user_id', user.id)
      .order('last_accessed_at', { ascending: false, nullsFirst: false });

    if (error) {
      console.warn('Failed to fetch enrollments:', error.message);
      return [];
    }

    return data as PathEnrollment[];
  },

  async enrollInPath(pathId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Get path details for total_courses count
    const { data: pathData } = await supabase
      .from('path_courses')
      .select('course_id')
      .eq('path_id', pathId);

    const { data, error } = await supabase
      .from('path_enrollments')
      .insert({
        path_id: pathId,
        user_id: user.id,
        status: 'enrolled',
        total_courses: pathData?.length || 0,
        enrolled_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as PathEnrollment;
  },

  async updatePathEnrollment(pathId: string, updates: Partial<PathEnrollment>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('path_enrollments')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('path_id', pathId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data as PathEnrollment;
  },

  // ---- Assessments ----
  async getAssessment(id: string) {
    const { data, error } = await supabase
      .from('assessments')
      .select(`
        *,
        questions:assessment_questions (*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    // Sort questions by order
    const assessment = data as Assessment;
    return {
      ...assessment,
      questions: (assessment.questions || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    } as Assessment;
  },

  async getModuleAssessments(moduleId: string) {
    const { data, error } = await supabase
      .from('assessments')
      .select('*')
      .eq('module_id', moduleId)
      .eq('status', 'published')
      .order('created_at', { ascending: true });

    if (error) {
      console.warn('Failed to fetch assessments:', error.message);
      return [];
    }

    return data as Assessment[];
  },

  async startAssessmentAttempt(assessmentId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Get current attempt count
    const { data: attempts } = await supabase
      .from('assessment_attempts')
      .select('attempt_number')
      .eq('assessment_id', assessmentId)
      .eq('user_id', user.id)
      .order('attempt_number', { ascending: false })
      .limit(1);

    const attemptNumber = (attempts?.[0]?.attempt_number || 0) + 1;

    const { data, error } = await supabase
      .from('assessment_attempts')
      .insert({
        assessment_id: assessmentId,
        user_id: user.id,
        attempt_number: attemptNumber,
        status: 'in_progress',
        started_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as AssessmentAttempt;
  },

  async submitAssessmentAttempt(attemptId: string, answers: Record<string, unknown>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Get the attempt and assessment
    const { data: attempt } = await supabase
      .from('assessment_attempts')
      .select('*, assessment:assessments(*, questions:assessment_questions(*))')
      .eq('id', attemptId)
      .maybeSingle();

    if (!attempt) throw new Error('Attempt not found');

    // Calculate score
    const assessment = (attempt as AssessmentAttempt & { assessment: Assessment }).assessment;
    const questions = assessment.questions || [];
    let score = 0;
    let maxScore = 0;

    questions.forEach((q: AssessmentQuestion) => {
      maxScore += q.points;
      const userAnswer = answers[q.id];
      // Simple comparison - extend for different question types
      if (JSON.stringify(userAnswer) === JSON.stringify(q.correct_answer)) {
        score += q.points;
      }
    });

    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const passed = percentage >= assessment.passing_score;

    const startedAt = new Date((attempt as AssessmentAttempt).started_at);
    const timeSpentSeconds = Math.round((Date.now() - startedAt.getTime()) / 1000);

    const { data, error } = await supabase
      .from('assessment_attempts')
      .update({
        answers,
        score,
        max_score: maxScore,
        percentage,
        passed,
        status: 'graded',
        submitted_at: new Date().toISOString(),
        time_spent_seconds: timeSpentSeconds,
        updated_at: new Date().toISOString(),
      })
      .eq('id', attemptId)
      .select()
      .single();

    if (error) throw error;
    return data as AssessmentAttempt;
  },

  async getMyAssessmentAttempts(assessmentId?: string) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('assessment_attempts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (assessmentId) {
      query = query.eq('assessment_id', assessmentId);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch assessment attempts:', error.message);
      return [];
    }

    return data as AssessmentAttempt[];
  },

  async getBestAssessmentScore(courseId: string): Promise<number | null> {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('assessment_attempts')
      .select('percentage, assessments!inner(course_id, module_id, lesson_id)')
      .eq('assessments.course_id', courseId)
      .is('assessments.module_id', null)
      .is('assessments.lesson_id', null)
      .eq('user_id', user.id)
      .not('percentage', 'is', null)
      .order('percentage', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch best assessment score:', error.message);
      return null;
    }

    return data?.percentage ?? null;
  },

  // ---- Certificates ----
  async getMyCertificates() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('certificates')
      .select(`
        *,
        path:learning_paths (id, name, thumbnail_url),
        course:courses (id, name, thumbnail_url)
      `)
      .eq('user_id', user.id)
      .order('issued_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch certificates:', error.message);
      return [];
    }

    return data as Certificate[];
  },

  // ---- Badges (Gamification) ----
  async getAllBadges() {
    const { data, error } = await supabase
      .from('badges')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.warn('Failed to fetch badges:', error.message);
      return [];
    }

    return data as Badge[];
  },

  async getMyBadges() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_badges')
      .select(`
        *,
        badge:badges (*)
      `)
      .eq('user_id', user.id)
      .order('earned_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch user badges:', error.message);
      return [];
    }

    return data as UserBadge[];
  },

  async getBadgesWithEarnedStatus(): Promise<BadgeWithEarnedStatus[]> {
    const user = await getSessionUser();

    // Get all active badges
    const { data: allBadges, error: badgesError } = await supabase
      .from('badges')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (badgesError) {
      console.warn('Failed to fetch badges:', badgesError.message);
      return [];
    }

    if (!user) {
      // Return all badges as unearned
      return (allBadges || []).map((b: Badge) => ({
        ...b,
        is_earned: false,
      }));
    }

    // Get user's earned badges
    const { data: earnedBadges, error: earnedError } = await supabase
      .from('user_badges')
      .select('badge_id, earned_at')
      .eq('user_id', user.id);

    if (earnedError) {
      console.warn('Failed to fetch earned badges:', earnedError.message);
    }

    // Create map of earned badge IDs to earned_at dates
    const earnedMap = new Map<string, string>();
    (earnedBadges || []).forEach((ub: { badge_id: string; earned_at: string }) => {
      earnedMap.set(ub.badge_id, ub.earned_at);
    });

    // Filter out secret badges that haven't been earned
    const visibleBadges = (allBadges || []).filter((b: Badge) =>
      !b.is_secret || earnedMap.has(b.id)
    );

    // Merge earned status
    return visibleBadges.map((b: Badge) => ({
      ...b,
      is_earned: earnedMap.has(b.id),
      earned_at: earnedMap.get(b.id),
    }));
  },

  // ---- Points (Gamification) ----
  async getMyPoints(): Promise<{ total: number; transactions: PointTransaction[] }> {
    const user = await getSessionUser();
    if (!user) return { total: 0, transactions: [] };

    const { data, error } = await supabase
      .from('point_transactions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch points:', error.message);
      return { total: 0, transactions: [] };
    }

    const transactions = (data || []) as PointTransaction[];

    // Total via DB aggregate: PostgREST caps a select at 1000 rows, so summing the fetched rows in JS
    // undercounts a user with >1000 transactions. get_user_points_total (mig 405) sums in Postgres.
    // Fall back to the fetched sum if the RPC is unavailable.
    const { data: totalData, error: totalError } = await supabase.rpc('get_user_points_total', {
      p_user: user.id,
    });
    const total = totalError
      ? transactions.reduce((sum, tx) => sum + (tx.points ?? 0), 0)
      : Number(totalData ?? 0);
    if (totalError) console.warn('Points total RPC failed, using fetched sum:', totalError.message);

    return { total, transactions };
  },

  // ---- Leaderboard (via edge function for cross-user data) ----
  async getLeaderboard(timeframe: 'weekly' | 'monthly' | 'all_time' = 'all_time'): Promise<{ entries: LeaderboardEntry[]; currentUserRank: number | null }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { entries: [], currentUserRank: null };

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/prometheus-gamification/leaderboard?timeframe=${timeframe}`,
      {
        headers: {
          ...EDGE_FUNCTION_HEADERS,
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      console.warn('Failed to fetch leaderboard:', response.statusText);
      return { entries: [], currentUserRank: null };
    }

    const result = await response.json();
    return {
      entries: result.entries || [],
      currentUserRank: result.currentUserRank ?? null,
    };
  },

  // ---- Challenges ----
  async getChallenges(): Promise<ChallengeWithStatus[]> {
    const user = await getSessionUser();

    const { data: challenges, error } = await supabase
      .from('challenges')
      .select('*')
      .eq('is_active', true)
      .in('status', ['active', 'upcoming'])
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch challenges:', error.message);
      return [];
    }

    if (!challenges || challenges.length === 0) return [];

    const challengeIds = challenges.map((c: Challenge) => c.id);

    // Get participant counts
    const { data: participants } = await supabase
      .from('challenge_participants')
      .select('challenge_id')
      .in('challenge_id', challengeIds);

    const participantCounts: Record<string, number> = {};
    for (const p of participants || []) {
      participantCounts[(p as { challenge_id: string }).challenge_id] = (participantCounts[(p as { challenge_id: string }).challenge_id] || 0) + 1;
    }

    // Get user's join status
    let myJoinedSet = new Set<string>();
    if (user) {
      const { data: myParticipations } = await supabase
        .from('challenge_participants')
        .select('challenge_id')
        .eq('user_id', user.id)
        .in('challenge_id', challengeIds);

      myJoinedSet = new Set((myParticipations || []).map((p: { challenge_id: string }) => p.challenge_id));
    }

    return challenges.map((c: Challenge) => ({
      ...c,
      participant_count: participantCounts[c.id!] || 0,
      is_joined: myJoinedSet.has(c.id!),
    })) as ChallengeWithStatus[];
  },

  async joinChallenge(challengeId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('challenge_participants')
      .upsert(
        {
          challenge_id: challengeId,
          user_id: user.id,
          progress: 0,
          joined_at: new Date().toISOString(),
        },
        { onConflict: 'challenge_id,user_id' },
      )
      .select()
      .single();

    if (error) throw error;
    return data as ChallengeParticipant;
  },

  async getMyChallenges() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('challenge_participants')
      .select('*, challenge:challenges(*)')
      .eq('user_id', user.id)
      .order('joined_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch my challenges:', error.message);
      return [];
    }

    return data || [];
  },

  // ---- Lesson Points Award ----
  async awardLessonPoints(lessonId: string, lessonName: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return; // silently skip if not authenticated

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    try {
      await fetch(
        `${supabaseUrl}/functions/v1/prometheus-gamification/complete-lesson`,
        {
          method: 'POST',
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ lesson_id: lessonId, lesson_name: lessonName }),
        },
      );
    } catch {
      // Non-critical: don't block lesson completion if points fail
      console.warn('Failed to award lesson points');
    }
  },

  // ---- Badge Evaluation Trigger ----
  async triggerBadgeEvaluation() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/prometheus-gamification/evaluate-badges`,
      {
        method: 'POST',
        headers: {
          ...EDGE_FUNCTION_HEADERS,
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    );

    if (!response.ok) {
      throw new Error(`Badge evaluation failed: ${response.statusText}`);
    }

    return response.json();
  },

  // ---- Legacy Learning Progress (for backward compat) ----
  async getUserLearningProgress(learningPathId?: string) {
    const user = await getSessionUser();
    if (!user) return [];

    // Map to path_enrollments for now
    let query = supabase
      .from('path_enrollments')
      .select('*')
      .eq('user_id', user.id);

    if (learningPathId) {
      query = query.eq('path_id', learningPathId);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch learning progress:', error.message);
      return [];
    }

    // Transform to legacy format
    return (data || []).map((enrollment: PathEnrollment) => ({
      id: enrollment.id,
      user_id: enrollment.user_id,
      learning_path_id: enrollment.path_id,
      module_id: '',
      status: enrollment.status === 'completed' ? 'completed' : enrollment.status === 'in_progress' ? 'in_progress' : 'not_started',
      progress_percent: enrollment.progress_percentage,
      score: null,
      time_spent_minutes: 0,
      completed_at: enrollment.completed_at,
      created_at: enrollment.created_at,
      updated_at: enrollment.updated_at,
    })) as UserLearningProgress[];
  },

  async updateLearningProgress(progress: Omit<UserLearningProgress, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Map to path_enrollments update
    const status = progress.status === 'completed' ? 'completed' :
                   progress.status === 'in_progress' ? 'in_progress' : 'enrolled';

    const { data, error } = await supabase
      .from('path_enrollments')
      .upsert({
        user_id: user.id,
        path_id: progress.learning_path_id,
        status,
        progress_percentage: progress.progress_percent,
        completed_at: progress.completed_at,
        last_accessed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Return in legacy format
    return {
      id: (data as PathEnrollment).id,
      user_id: (data as PathEnrollment).user_id,
      learning_path_id: (data as PathEnrollment).path_id,
      module_id: progress.module_id,
      status: progress.status,
      progress_percent: progress.progress_percent,
      score: progress.score,
      time_spent_minutes: progress.time_spent_minutes,
      completed_at: (data as PathEnrollment).completed_at,
      created_at: (data as PathEnrollment).created_at,
      updated_at: (data as PathEnrollment).updated_at,
    } as UserLearningProgress;
  },

  // ---- Coaching & Guidance ----
  async getCoaches(specialty?: string) {
    let query = supabase
      .from('coaches')
      .select('*')
      .eq('is_active', true)
      .order('rating', { ascending: false });

    if (specialty) {
      query = query.contains('specialties', [specialty]);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch coaches:', error.message);
      return [];
    }

    return data as Coach[];
  },

  async getCoach(id: string) {
    const { data, error } = await supabase
      .from('coaches')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Coach;
  },

  async getGuidanceCalls(filters?: { status?: GuidanceCall['status']; upcoming?: boolean }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('guidance_calls')
      .select('*, coach:coaches(*)')
      .eq('user_id', user.id)
      .order('scheduled_at', { ascending: true });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.upcoming) {
      query = query.gte('scheduled_at', new Date().toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch guidance calls:', error.message);
      return [];
    }

    return data as (GuidanceCall & { coach: Coach | null })[];
  },

  async getUpcomingCalls(limit = 5) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('guidance_calls')
      .select('*, coach:coaches(*)')
      .eq('user_id', user.id)
      .eq('status', 'scheduled')
      .gte('scheduled_at', new Date().toISOString())
      .order('scheduled_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch upcoming calls:', error.message);
      return [];
    }

    return data as (GuidanceCall & { coach: Coach | null })[];
  },

  async bookGuidanceCall(call: Omit<GuidanceCall, 'id' | 'user_id' | 'status' | 'created_at' | 'updated_at'>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('guidance_calls')
      .insert({
        user_id: user.id,
        status: 'scheduled',
        ...call,
      })
      .select()
      .single();

    if (error) throw error;
    return data as GuidanceCall;
  },

  async updateGuidanceCall(id: string, updates: Partial<GuidanceCall>) {
    const { data, error } = await supabase
      .from('guidance_calls')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as GuidanceCall;
  },

  async cancelGuidanceCall(id: string) {
    const { error } = await supabase
      .from('guidance_calls')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Activity Feed ----
  async getRecentActivity(options?: {
    entityType?: string;      // Filter by lesson/course/asset/task
    sinceHours?: number;       // Default 24
    limit?: number;            // Default 20
  }) {
    const { entityType, sinceHours = 24, limit = 20 } = options || {};

    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('activities')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    // Time window filter
    const since = new Date(Date.now() - sinceHours * 60 * 60 * 1000);
    query = query.gte('created_at', since.toISOString());

    // Entity type filter
    if (entityType) {
      query = query.eq('entity_type', entityType);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch activity:', error.message);
      return [];
    }

    return data as Activity[];
  },

  async logActivity(
    activity: Omit<Activity, 'id' | 'user_id' | 'created_at'>,
    opts?: { keepalive?: boolean },
  ) {
    // While the page is closing: no await, no returned row (Speedies' session-end rows).
    if (opts?.keepalive) {
      restInsertKeepalive('activities', activity as unknown as Record<string, unknown>);
      return undefined;
    }
    const user = await getSessionUser();
    if (!user) {
      // Expected pre-auth (e.g. the guarded-route flash before the login
      // redirect) — callers swallow this; don't log it as an error.
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('activities')
      .insert({
        user_id: user.id,
        ...activity,
      })
      .select()
      .single();

    if (error) {
      // Real insert failure — keep visible since every caller swallows the throw.
      console.error('[logActivity] insert failed:', error);
      throw error;
    }

    return data as Activity;
  },

  /**
   * One Speedy playback summary row, sent with keepalive when a slide leaves (F-129). The shape is
   * the `speedy_playback_sessions` table (hermes migration 696); the database checks the ranges.
   */
  insertPlaybackSession(row: {
    speedy_id: string;
    started_at: string;
    ended_at: string;
    ttff_ms: number | null;
    stall_count: number;
    stall_ms: number;
    from_preload: boolean;
    start_level: number | null;
    max_level: number | null;
    pass_renewals: number;
    errors: number;
    engine: 'hlsjs' | 'native';
    device: 'ios' | 'android' | 'desktop' | 'other' | null;
  }): void {
    // Clamp to the table's checks so one odd measurement can't lose the whole row.
    const clamp = (n: number, max: number) => Math.min(Math.max(Math.round(n), 0), max);
    row = {
      ...row,
      ttff_ms: row.ttff_ms === null ? null : clamp(row.ttff_ms, 120_000),
      stall_count: clamp(row.stall_count, 1000),
      stall_ms: clamp(row.stall_ms, 3_600_000),
    };
    restInsertKeepalive('speedy_playback_sessions', row);
  },

  // ---- Action Plan Sessions ----
  async getActionPlanSessions(filters?: { status?: ActionPlanSession['status']; upcoming?: boolean }) {
    const user = await getSessionUser();
    if (!user) return [];

    let query = supabase
      .from('action_plan_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('scheduled_at', { ascending: true });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.upcoming) {
      query = query.gte('scheduled_at', new Date().toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Failed to fetch action plan sessions:', error.message);
      return [];
    }

    return data as ActionPlanSession[];
  },

  async getUpcomingSessions(limit = 5) {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('action_plan_sessions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'upcoming')
      .gte('scheduled_at', new Date().toISOString())
      .order('scheduled_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.warn('Failed to fetch upcoming sessions:', error.message);
      return [];
    }

    return data as ActionPlanSession[];
  },

  // ---- Settings ----
  async getUserSettings() {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch user settings:', error.message);
      return null;
    }

    return data as UserSettings;
  },

  async updateUserSettings(updates: Partial<UserSettings>) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_settings')
      .upsert({
        user_id: user.id,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as UserSettings;
  },

  // ===========================================
  // Author CRUD Operations (LMS Content Management)
  // ===========================================

  // ---- Learning Path CRUD ----
  async createLearningPath(
    data: Omit<LearningPath, 'id' | 'created_at' | 'updated_at' | 'published_at' | 'course_count'>
  ): Promise<LearningPath> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data: result, error } = await supabase
      .from('learning_paths')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return result as LearningPath;
  },

  async updateLearningPath(
    id: string,
    updates: Partial<Omit<LearningPath, 'id' | 'created_at' | 'created_by'>>
  ): Promise<LearningPath> {
    const { data, error } = await supabase
      .from('learning_paths')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as LearningPath;
  },

  async deleteLearningPath(id: string): Promise<void> {
    const { error } = await supabase
      .from('learning_paths')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async publishLearningPath(id: string): Promise<LearningPath> {
    const { data, error } = await supabase
      .from('learning_paths')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as LearningPath;
  },

  // ---- Course CRUD ----
  async createCourse(
    data: Omit<Course, 'id' | 'created_at' | 'updated_at' | 'modules' | 'lessons' | 'module_count' | 'lesson_count'>
  ): Promise<Course> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data: result, error } = await supabase
      .from('courses')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result as Course;
  },

  async updateCourse(
    id: string,
    updates: Partial<Omit<Course, 'id' | 'created_at'>>
  ): Promise<Course> {
    const { data, error } = await supabase
      .from('courses')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Course;
  },

  async deleteCourse(id: string): Promise<void> {
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Module CRUD ----
  async createModule(
    data: Omit<Module, 'id' | 'created_at' | 'updated_at' | 'lessons' | 'lesson_count'>
  ): Promise<Module> {
    const { data: result, error } = await supabase
      .from('modules')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result as Module;
  },

  async updateModule(
    id: string,
    updates: Partial<Omit<Module, 'id' | 'created_at'>>
  ): Promise<Module> {
    const { data, error } = await supabase
      .from('modules')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Module;
  },

  async deleteModule(id: string): Promise<void> {
    const { error } = await supabase
      .from('modules')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async reorderModules(courseId: string, moduleIds: string[]): Promise<void> {
    // Update each module with its new order
    const updates = moduleIds.map((id, index) =>
      supabase
        .from('modules')
        .update({ order: index, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('course_id', courseId)
    );

    await Promise.all(updates);
  },

  // ---- Lesson CRUD ----
  async createLesson(
    data: Omit<Lesson, 'id' | 'created_at' | 'updated_at'>
  ): Promise<Lesson> {
    const { data: result, error } = await supabase
      .from('lessons')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result as Lesson;
  },

  async updateLesson(
    id: string,
    updates: Partial<Omit<Lesson, 'id' | 'created_at'>>
  ): Promise<Lesson> {
    const { data, error } = await supabase
      .from('lessons')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Lesson;
  },

  async deleteLesson(id: string): Promise<void> {
    const { error } = await supabase
      .from('lessons')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async reorderLessons(moduleId: string, lessonIds: string[]): Promise<void> {
    const updates = lessonIds.map((id, index) =>
      supabase
        .from('lessons')
        .update({ order: index, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('module_id', moduleId)
    );

    await Promise.all(updates);
  },

  // ---- Path-Course Association ----
  async addCourseToPath(pathId: string, courseId: string, order: number, isRequired = true): Promise<void> {
    const { error } = await supabase
      .from('path_courses')
      .insert({
        path_id: pathId,
        course_id: courseId,
        order,
        is_required: isRequired,
      });

    if (error) throw error;
  },

  async removeCourseFromPath(pathId: string, courseId: string): Promise<void> {
    const { error } = await supabase
      .from('path_courses')
      .delete()
      .eq('path_id', pathId)
      .eq('course_id', courseId);

    if (error) throw error;
  },

  async reorderPathCourses(pathId: string, courseIds: string[]): Promise<void> {
    const updates = courseIds.map((courseId, index) =>
      supabase
        .from('path_courses')
        .update({ order: index })
        .eq('path_id', pathId)
        .eq('course_id', courseId)
    );

    await Promise.all(updates);
  },

  // ---- Assessment CRUD ----
  async createAssessment(
    data: Omit<Assessment, 'id' | 'created_at' | 'updated_at' | 'questions' | 'question_count'>
  ): Promise<Assessment> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { data: result, error } = await supabase
      .from('assessments')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return result as Assessment;
  },

  async updateAssessment(
    id: string,
    updates: Partial<Omit<Assessment, 'id' | 'created_at' | 'created_by'>>
  ): Promise<Assessment> {
    const { data, error } = await supabase
      .from('assessments')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Assessment;
  },

  async deleteAssessment(id: string): Promise<void> {
    const { error } = await supabase
      .from('assessments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // ---- Assessment Question CRUD ----
  async createQuestion(
    data: Omit<AssessmentQuestion, 'id' | 'created_at' | 'updated_at'>
  ): Promise<AssessmentQuestion> {
    const { data: result, error } = await supabase
      .from('assessment_questions')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result as AssessmentQuestion;
  },

  async updateQuestion(
    id: string,
    updates: Partial<Omit<AssessmentQuestion, 'id' | 'created_at'>>
  ): Promise<AssessmentQuestion> {
    const { data, error } = await supabase
      .from('assessment_questions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as AssessmentQuestion;
  },

  async deleteQuestion(id: string): Promise<void> {
    const { error } = await supabase
      .from('assessment_questions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async reorderQuestions(assessmentId: string, questionIds: string[]): Promise<void> {
    const updates = questionIds.map((id, index) =>
      supabase
        .from('assessment_questions')
        .update({ order: index, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('assessment_id', assessmentId)
    );

    await Promise.all(updates);
  },

  // ===========================================
  // Progress Tracking Mutations
  // ===========================================

  // ---- Lesson Progress ----
  //
  // Both writes go through rpc_record_lesson_progress (migration 530), which upserts
  // course_enrollments BEFORE lesson_progress inside one transaction.
  //
  // They used to write lesson_progress directly and leave the enrollment to
  // trigger_update_course_progress. That trigger's `UPDATE … WHERE user_id AND course_id`
  // matches zero rows when no enrollment exists yet — silently, because an UPDATE
  // affecting nothing is not an error. Reaching a lesson by any route other than the
  // course landing page (deep link, Resume, notification, next-lesson nav) therefore
  // recorded progress against an enrollment that did not exist, and the row created
  // afterwards sat at 0% forever. Twelve prod enrollments ended up in that state.
  //
  // The RPC also owns current_lesson_id and last_accessed_at, which raced the same way.
  async markLessonComplete(lessonId: string, courseId: string): Promise<void> {
    const { error } = await supabase.rpc('rpc_record_lesson_progress', {
      p_lesson_id: lessonId,
      p_course_id: courseId,
      p_status: 'completed',
    });

    if (error) throw error;
  },

  async markLessonStarted(lessonId: string, courseId: string): Promise<void> {
    // Safe to call on a lesson already completed — the RPC never regresses a
    // completed lesson back to in_progress.
    const { error } = await supabase.rpc('rpc_record_lesson_progress', {
      p_lesson_id: lessonId,
      p_course_id: courseId,
      p_status: 'in_progress',
    });

    if (error) throw error;
  },

  async updateLessonProgress(
    lessonId: string,
    progress: { progress_percentage?: number; time_spent_seconds?: number }
  ): Promise<void> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Look up the prior progress so we can detect milestone crossings (25/50/75%)
    // and emit a single `progressed` activity per crossing — never per-keystroke.
    let priorPct: number | null = null;
    if (typeof progress.progress_percentage === 'number') {
      const { data: existing } = await supabase
        .from('lesson_progress')
        .select('progress_percentage')
        .eq('user_id', user.id)
        .eq('lesson_id', lessonId)
        .maybeSingle();
      priorPct = (existing?.progress_percentage as number | undefined) ?? null;
    }

    const { error } = await supabase
      .from('lesson_progress')
      .update({
        ...progress,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .eq('lesson_id', lessonId);

    if (error) throw error;

    if (typeof progress.progress_percentage === 'number') {
      const newPct = progress.progress_percentage;
      const milestones = [25, 50, 75];
      const crossed = milestones.find(
        (m) => newPct >= m && (priorPct === null || priorPct < m),
      );
      if (crossed !== undefined) {
        this.logActivity({
          type: 'learning',
          action: 'progressed',
          entity_type: 'lesson',
          entity_id: lessonId,
          entity_name: lessonId,
          description: `Crossed ${crossed}% on lesson ${lessonId}`,
          metadata: {
            milestone_pct: crossed,
            new_progress_pct: newPct,
            time_spent_seconds: progress.time_spent_seconds,
          },
        }).catch(() => {
          // Non-critical.
        });
      }
    }
  },

  async getLessonProgress(courseId: string): Promise<Array<{
    lesson_id: string;
    status: string;
    progress_percentage: number;
    time_spent_seconds: number;
    completed_at: string | null;
  }>> {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('lesson_id, status, progress_percentage, time_spent_seconds, completed_at')
      .eq('user_id', user.id)
      .eq('course_id', courseId);

    if (error) {
      console.warn('Failed to fetch lesson progress:', error.message);
      return [];
    }

    return data || [];
  },

  // ---- Course Progress ----
  //
  // `updateCourseProgress` was removed in the 2026-08-22 progress fix. It was a SECOND
  // client-side writer of course_enrollments.{progress_percentage, completed_lessons_count,
  // status, completed_at}, duplicating the formula owned by trigger_update_course_progress.
  // It had zero callers, but wiring it up would have raced the trigger and — because it set
  // status='completed' — could have fired trg_course_completed → announce-course-completion
  // from the browser. The aggregate has exactly one author: the trigger, via
  // recompute_course_progress(). Derive for display; see getMyCourseEnrollments above.

  async enrollInCourse(courseId: string): Promise<void> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('course_enrollments')
      .insert({
        user_id: user.id,
        course_id: courseId,
        status: 'not_started',
        progress_percentage: 0,
        completed_lessons_count: 0,
        enrolled_at: new Date().toISOString(),
      });

    // Ignore duplicate enrollment error
    if (error && error.code !== '23505') throw error;

    if (!error) {
      // Only emit on first-time enrollment, not on duplicate.
      this.logActivity({
        type: 'learning',
        action: 'enrolled',
        entity_type: 'course',
        entity_id: courseId,
        entity_name: courseId,
        description: `Enrolled in course ${courseId}`,
        metadata: {},
      }).catch(() => {
        // Non-critical.
      });
    }
  },

  async getCourseEnrollment(courseId: string): Promise<{
    status: string;
    progress_percentage: number;
    completed_lessons_count: number;
    current_lesson_id: string | null;
    enrolled_at: string;
    completed_at: string | null;
  } | null> {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('course_enrollments')
      .select('status, progress_percentage, completed_lessons_count, current_lesson_id, enrolled_at, completed_at')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch course enrollment:', error.message);
      return null;
    }

    return data;
  },

  async getMyCourseEnrollments() {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('course_enrollments')
      .select(`
        *,
        course:courses (
          id,
          name,
          description,
          thumbnail_url,
          preview_clip_url,
          pillar,
          status,
          lessons_count,
          duration_minutes,
          difficulty_level,
          thumbnail_media:media!courses_thumbnail_asset_id_fkey (
            id,
            blurhash,
            small_poster_url,
            poster_url
          ),
          lesson_posters:lessons!lessons_course_id_courses_id_fk (
            id,
            video_media:media!lessons_video_asset_id_media_id_fk (
              small_poster_url,
              poster_url,
              blurhash
            )
          )
        )
      `)
      .eq('user_id', user.id)
      .order('last_accessed_at', { ascending: false, nullsFirst: false })
      .order('enrolled_at', { ascending: false });

    if (error) {
      console.warn('Failed to fetch course enrollments:', error.message);
      return [];
    }

    const enrollments = data || [];
    if (enrollments.length === 0) return enrollments;

    // Overwrite the stored aggregate with a DERIVED one before anything renders it.
    //
    // course_enrollments.progress_percentage is written once by a trigger on two
    // specific lesson_progress transitions and never recomputed — not when a
    // course gains lessons, not when the enrollment was created after progress
    // already existed. On prod it had drifted on 14 enrollments, 11 of them
    // reading 0% for a fully completed course.
    //
    // Every list surface (Home, Learn, LearningProgressWidget) renders whatever
    // this returns, so deriving here fixes all of them at once and keeps the
    // "payload carries the truth" contract in one place rather than asking each
    // component to re-derive.
    //
    // Denominator is the real lesson rows already embedded in the select, NOT
    // courses.lessons_count — that is itself a denormalisation and is currently
    // wrong for 1 of 38 courses on prod, so deriving from it would just move the
    // bug rather than remove it.
    const courseIds = enrollments
      .map((e: { course_id: string }) => e.course_id)
      .filter(Boolean);

    const { data: completedRows, error: progressError } = await supabase
      .from('lesson_progress')
      .select('course_id')
      .eq('user_id', user.id)
      .eq('status', 'completed')
      .in('course_id', courseIds);

    if (progressError) {
      // Fall back to the stored value rather than showing nothing. It may be
      // stale, but a wrong number beats an empty course list.
      console.warn('Failed to derive course progress, using stored value:', progressError.message);
      return enrollments;
    }

    const completedByCourse = new Map<string, number>();
    for (const row of (completedRows ?? []) as { course_id: string | null }[]) {
      if (!row.course_id) continue;
      completedByCourse.set(row.course_id, (completedByCourse.get(row.course_id) ?? 0) + 1);
    }

    return enrollments.map((enrollment: Record<string, unknown>) => {
      const courseId = enrollment.course_id as string;
      const course = enrollment.course as { lesson_posters?: unknown[] } | null;
      const totalLessons = course?.lesson_posters?.length ?? 0;
      const completed = completedByCourse.get(courseId) ?? 0;

      // No lessons yet (unimported course) — leave the stored value alone rather
      // than asserting 0%, which would read as regression on a course that has
      // simply not been populated.
      if (totalLessons === 0) return enrollment;

      return {
        ...enrollment,
        progress_percentage: Math.round((completed / totalLessons) * 100),
        completed_lessons_count: completed,
      };
    });
  },

  // ---- Path Progress ----
  async updatePathProgress(pathId: string): Promise<void> {
    const user = await getSessionUser();
    if (!user) return;

    // Get total courses in path
    const { data: pathCourses } = await supabase
      .from('path_courses')
      .select('course_id')
      .eq('path_id', pathId);

    const totalCourses = pathCourses?.length || 0;
    if (totalCourses === 0) return;

    // Get completed courses (courses where user has 100% progress)
    const courseIds = pathCourses?.map(pc => pc.course_id) || [];
    const { data: enrollments } = await supabase
      .from('course_enrollments')
      .select('course_id, status')
      .eq('user_id', user.id)
      .in('course_id', courseIds)
      .eq('status', 'completed');

    const completedCourses = enrollments?.length || 0;
    const progressPercentage = Math.round((completedCourses / totalCourses) * 100);

    // Update path enrollment
    const { error } = await supabase
      .from('path_enrollments')
      .update({
        progress_percentage: progressPercentage,
        completed_courses: completedCourses,
        total_courses: totalCourses,
        status: progressPercentage >= 100 ? 'completed' : 'in_progress',
        completed_at: progressPercentage >= 100 ? new Date().toISOString() : null,
        last_accessed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .eq('path_id', pathId);

    if (error) {
      console.warn('Failed to update path progress:', error.message);
    }
  },

  // ---- User Roles ----
  async getMyRoles() {
    const user = await getSessionUser();
    if (!user) return [];

    // Check global roles first (super_admin, admin, user)
    const { data: globalRole, error: globalError } = await supabase
      .from('global_user_roles')
      .select('id, user_id, role, created_at')
      .eq('user_id', user.id)
      .maybeSingle();

    if (globalError) {
      console.warn('Failed to fetch global role:', globalError.message);
    }

    // If user has global super_admin or admin role, return it with synthetic role object
    if (globalRole && (globalRole.role === 'super_admin' || globalRole.role === 'admin')) {
      return [{
        id: globalRole.id,
        user_id: globalRole.user_id,
        role_id: globalRole.id, // Use global role ID as synthetic role_id
        organization_id: null,
        target_company_id: null,
        status: 'active',
        assigned_at: globalRole.created_at,
        expires_at: null,
        role: {
          id: globalRole.id,
          code: globalRole.role,
          name: globalRole.role === 'super_admin' ? 'Super Admin' : 'Admin',
          scope: 'global',
          role_type: 'internal'
        }
      }] as UserRoleAssignment[];
    }

    // Otherwise check org-specific role assignments
    const { data, error } = await supabase
      .from('user_role_assignments')
      .select(`
        id,
        user_id,
        role_id,
        organization_id,
        target_company_id,
        status,
        assigned_at,
        expires_at,
        role:roles (
          id,
          code,
          name,
          scope,
          role_type
        )
      `)
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (error) {
      console.warn('Failed to fetch user roles:', error.message);
      return [];
    }

    // Supabase returns role as single object when using foreign key relation
    // but TypeScript infers it as array, so we need to cast properly
    return (data ?? []).map((item) => ({
      ...item,
      role: Array.isArray(item.role) ? item.role[0] : item.role,
    })) as UserRoleAssignment[];
  },

  // ===========================================
  // Media Upload
  // ===========================================

  async initMediaUpload(
    filename: string,
    size: number,
    mimeType: string,
    purpose?: MediaAssetPurpose,
    organizationId?: string
  ): Promise<InitUploadResponse> {
    const { data, error } = await supabase.functions.invoke('media-upload', {
      region: EDGE_FUNCTION_INVOKE_REGION,
      body: {
        filename,
        size,
        mimeType,
        purpose,
        organizationId,
      },
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    // The invoke method uses POST by default, but we need to add query param
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media-upload?action=init`,
      {
        method: 'POST',
        headers: {
          ...EDGE_FUNCTION_HEADERS,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          filename,
          size,
          mimeType,
          purpose,
          organizationId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to initialize upload');
    }

    return response.json();
  },

  async completeMediaUpload(assetId: string): Promise<CompleteUploadResponse> {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media-upload?action=complete`,
      {
        method: 'POST',
        headers: {
          ...EDGE_FUNCTION_HEADERS,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ assetId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to complete upload');
    }

    return response.json();
  },

  async getMediaAsset(id: string): Promise<MediaAsset | null> {
    const { data, error } = await supabase
      .from('media')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;

    return data as MediaAsset;
  },

  async getMediaAssets(filters?: {
    purpose?: MediaAssetPurpose;
    status?: MediaAssetStatus;
    assetType?: MediaAssetType;
  }): Promise<MediaAsset[]> {
    let query = supabase
      .from('media')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.purpose) {
      query = query.eq('purpose', filters.purpose);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.assetType) {
      query = query.eq('asset_type', filters.assetType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []) as MediaAsset[];
  },

  async uploadFileToStorage(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  // ---- Voice Settings ----
  async getUserVoiceSettings(): Promise<UserVoiceSettings | null> {
    const user = await getSessionUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_voice_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.warn('Failed to fetch voice settings:', error);
      return null;
    }

    return data ? (snakeToCamel(data as Record<string, unknown>) as UserVoiceSettings) : null;
  },

  async updateUserVoiceSettings(settings: Partial<NewUserVoiceSettings>): Promise<UserVoiceSettings | null> {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    // Check if settings exist
    const { data: existing } = await supabase
      .from('user_voice_settings')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    const wirePayload = camelToSnake(settings as Record<string, unknown>);

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('user_voice_settings')
        .update({
          ...wirePayload,
          synced_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return snakeToCamel(data as Record<string, unknown>) as UserVoiceSettings;
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('user_voice_settings')
        .insert({
          user_id: user.id,
          ...wirePayload,
        })
        .select()
        .single();

      if (error) throw error;
      return snakeToCamel(data as Record<string, unknown>) as UserVoiceSettings;
    }
  },

  // ---- Asset Favorites ----
  async addAssetToFavorites(assetId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_asset_favorites')
      .insert({ user_id: user.id, asset_id: assetId });

    if (error) throw error;
  },

  async removeAssetFromFavorites(assetId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_asset_favorites')
      .delete()
      .match({ user_id: user.id, asset_id: assetId });

    if (error) throw error;
  },

  async getUserFavoriteAssets(): Promise<string[]> {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_asset_favorites')
      .select('asset_id')
      .eq('user_id', user.id);

    if (error) throw error;
    return data.map(f => f.asset_id);
  },

  async canUserSetFeatured(): Promise<boolean> {
    const user = await getSessionUser();
    if (!user) return false;

    // Check if user has content_admin role (has write permission on assets)
    const { data: roleAssignments } = await supabase
      .from('user_role_assignments')
      .select('role:roles(name)')
      .eq('user_id', user.id);

    if (!roleAssignments) return false;

    const hasAdminRole = roleAssignments.some((assignment: any) =>
      ['content_admin', 'super_admin'].includes(assignment.role?.name)
    );

    if (hasAdminRole) return true;

    // Check if user is an organization admin
    const { data: orgMembers } = await supabase
      .from('organization_members')
      .select('role')
      .eq('user_id', user.id);

    return orgMembers?.some((member: any) => member.role === 'admin') || false;
  },

  async updateAssetFeatured(assetId: string, featured: boolean) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('assets')
      .update({ featured })
      .eq('id', assetId);

    if (error) {
      // RLS will reject if user doesn't have permission
      throw new Error('Failed to update featured status - insufficient permissions');
    }
  },

  // ---- Asset Downloads ----
  /**
   * Download an asset file via the edge function proxy (avoids CORS / hides external URL).
   * Returns a Blob that can be handed to useFileDownload.
   */
  async downloadAssetFile(assetId: string): Promise<{ blob: Blob; filename: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prometheus-assets?action=download-file&id=${encodeURIComponent(assetId)}`,
      {
        method: 'GET',
        headers: { ...EDGE_FUNCTION_HEADERS, 'Authorization': `Bearer ${session?.access_token}` },
      },
    );
    if (!response.ok) throw new Error(`Download failed (${response.status})`);

    // Extract filename from Content-Disposition header
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="(.+?)"/);
    const filename = match?.[1] || 'download';

    return { blob: await response.blob(), filename };
  },

  async trackAssetDownload(assetId: string) {
    const user = await getSessionUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_asset_downloads')
      .upsert(
        { user_id: user.id, asset_id: assetId },
        { onConflict: 'user_id,asset_id' }
      );

    if (error) throw error;
  },

  async getUserDownloadedAssets(): Promise<string[]> {
    const user = await getSessionUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_asset_downloads')
      .select('asset_id')
      .eq('user_id', user.id);

    if (error) throw error;
    return data.map(d => d.asset_id);
  },

  // ---- Access Matrix ----
  async getAccessMatrix(): Promise<AccessMatrixItem[]> {
    const { data: assets, error: assetsError } = await supabase
      .from('assets')
      .select('id, name, asset_type, pillar, status, access_level, visibility, organization_id, updated_at')
      .order('name');

    if (assetsError) throw assetsError;

    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('id, name, pillar, status, access_level, visibility, organization_id, updated_at')
      .order('name');

    if (coursesError) throw coursesError;

    return [
      ...(assets || []).map(a => ({ ...a, type: 'asset' as const })),
      ...(courses || []).map(c => ({ ...c, type: 'course' as const })),
    ];
  },
};

export default api;
